import { renderFormattedText } from "../components/FormattedText";
import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  arrayUnion,
  onSnapshot,
  query,
  orderBy,
  limit,
  getDoc,
  setDoc,
  addDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, app } from "../firebaseConfig";
import "./global.css";
import "./HomePipeline.css";
import "./TechJobTile.css";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAuth } from "firebase/auth";
import { useLanguage, TranslatedText, LanguageToggle, localeFor } from "../i18n";
import moment from "moment";
import { useNavigate, useLocation } from "react-router-dom";
import { readLinkParams, useLinkedTab } from "../utils/appLinks";
import HowToGuide from "../components/HowToGuide";
import { addJobTotal } from "../utils/addJobTotal";
import { filterStaffForDropdowns } from "../utils/staffSettingsUtils";
import ClockPanel from "../clock/ClockPanel";
import ClockCalendar from "../clock/ClockCalendar";
import { useClockInLogic } from "../clock/useClockInLogic";
import { useJobVisits } from "../clock/jobVisits";
import { blockIfViewingAs, getViewAs, isViewingAs, startViewAs } from "../utils/viewAs";
import ViewAsPicker from "../components/ViewAsPicker";
import { JobCheckInButton, ActiveJobBanner, JobVisitHistory, JobOpenCheckInPrompt } from "../clock/JobCheckIn";
import { useJobFenceAuto, FenceAutoNotice } from "../clock/JobFenceAuto";
import JobLocationConfirm, { JobFenceStatus } from "../components/JobLocationConfirm";
import JobMoneyPanel from "../components/JobMoneyPanel";
import { JobDetailHeader, DetailFacts } from "../components/JobDetailPopup";
import { useChecklistAccess } from "../utils/checklistAccess";
import LinkedJobsButton from "../components/LinkedJobsButton";
import SubPayPanel from "../components/SubPayPanel";
import SubPayTermsInline from "../components/SubPayTermsInline";
import MyPayPanel from "../components/MyPayPanel";
import { canSeeSubPay } from "../utils/subPayTerms";
import { rollSubPayTermsOnto } from "../utils/subPayPayments";
import { isSubcontractor } from "../utils/contractorIdentity";
import {
  computeContractTotals,
  correctOriginalTotal,
  ledgerOwnsTotal,
  loadContractLedger,
  pushNewTotalToOpenInvoices,
} from "../utils/jobContractLedger";
import { lazyWithFallback } from "../utils/lazyWithFallback";
import { getOccurrencesForJob } from "../utils/getOccurrencesForJob";
import { materialsNeededForWeeks } from "../utils/materialsForWeek";
import TeamMessaging from "./TeamMessaging";
import { applyOccurrenceOverrideToRenderedJob, getOccurrenceOverrideRenderJobs, mergeRenderedRecurringJobs, saveRecurringJobEdit, closeOrDeleteJob, getRecurringOccurrenceKey, isOccurrenceCompletedFor, markOccurrenceCompleted } from "../utils/recurringEditUtils";
import { confirmAndSendCompletionEmail } from "../utils/completionEmail";
import QuickJobFieldEditor from "../components/QuickJobFieldEditor";
import CollapsibleActionSection from "../components/CollapsibleActionSection";
import JobHistoryPopup from "../components/JobHistoryPopup";
import {
  getMaterialsGroupId,
  fetchAllJobMaterials,
  loadMaterialsForJob,
  saveMaterialsForJob,
} from "../utils/materialsUtils";
import { useServiceTypes, withCurrentValue } from "../utils/serviceTypeOptions";
import BillingTypePopup from "../components/BillingTypePopup";
import DuplicateJobPopup from "../components/DuplicateJobPopup";
import {
  ensureJobsForDepositPayments,
  isAwaitingInvoice,
  getContractIdForJob,
  getRefreshedHeaderPatch,
  loadContractInvoices,
} from "../utils/billingFlow";
import { getWOStem, getNextContinuedWO, stripForContinuation } from "../utils/continueJobUtils";
import {
  TechCompleteDialog,
  TechCompletionStatus,
  TechCompletionQueue,
  useTechCompletionRequest,
} from "../utils/techCompletion";
import MissedJobsReview, { MissedJobsBanner } from "../components/MissedJobsReview";
import JobRemindersReview, { JobReminderBanner } from "../components/JobRemindersReview";
import { useJobReminders } from "../utils/useJobReminders";
import { readReminderSnooze, snoozeReminderAll, snoozeReminderKeys } from "../utils/jobReminders";
import {
  applyMissedPatches,
  buildMissedJobRows,
  missedKeyForRenderedJob,
  readSnooze,
  snoozeAllForToday,
  snoozeKeysForToday,
  usePendingCompletionIds,
} from "../utils/missedJobs";

// Tabs inside Home load the first time they are opened, so the Home screen
// itself downloads and starts faster (especially on phones in the field).
const ClockTime = lazyWithFallback(() => import("./ClockTime"));
const EmbeddedCalendar = lazyWithFallback(() => import("../calendar/EmbeddedCalendar"));
const CustomerCodes = lazyWithFallback(() => import("./CustomerCodes"));
const Maps = lazyWithFallback(() => import("./Maps"));
const TimeOff = lazyWithFallback(() => import("./TimeOff"));
const WarehouseInventory = lazyWithFallback(() => import("../inventory/WarehouseInventory"));
// Rough Estimate Builder moved to Team Dashboard (JobHistory.js).


// Retries a Firestore read a couple of times before giving up - field phones
// often drop the first request on a weak signal.
const withRetry = async (fn, tries = 3) => {
  let lastError;
  for (let attempt = 0; attempt < tries; attempt += 1) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      await new Promise((resolve) => setTimeout(resolve, 700 * (attempt + 1)));
    }
  }
  throw lastError;
};

const LoadingSpinner = ({ label = "Saving" }) => (
  <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
    <style>{`@keyframes jmc-note-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    <span
      aria-hidden="true"
      style={{
        width: 14,
        height: 14,
        border: "2px solid rgba(255,255,255,0.65)",
        borderTopColor: "#111827",
        borderRadius: "50%",
        display: "inline-block",
        animation: "jmc-note-spin 0.8s linear infinite",
      }}
    />
    {label}
  </span>
);

// Service Type options come from Settings -> Service Types (see useServiceTypes below).

const statusOptions = ["Active", "Completed", "Invoice Sent", "On Hold"];

const timeWindowOptions = [
  "ANY",
  "8AM - 10AM",
  "9AM - 11AM",
  "10AM - 12PM",
  "11AM - 1PM",
  "12PM - 2PM",
  "1PM - 3PM",
  "2PM - 4PM",
  "3PM - 5PM",
  "4PM - 6PM",
  "Custom Time"
];

// ✅ Always treat startDate as a DATE-ONLY in local time (no timezone shifting)
// ✅ Canonical date key (YYYY-MM-DD) in LOCAL time, no timezone shifting
/* ---------------------------------------------------------------------------
   ADMIN PIPELINE
   ---------------------------------------------------------------------------
   What is booked and not yet done, by start date. Forward looking, so the
   ranges cover WHOLE weeks rather than stopping at today the way the invoice
   totals do - next week's number is the point of the thing.

   Active jobs only, keyed on the job's own startDate. A recurring job is one
   document, so it is counted ONCE at its start date rather than once per
   occurrence. Counting every occurrence would multiply a single contract into
   a pipeline figure nobody could reconcile.
--------------------------------------------------------------------------- */
const PIPELINE_RANGES = [
  { id: "today", label: "Today" },
  { id: "tomorrow", label: "Tomorrow" },
  { id: "week", label: "This week" },
  { id: "nextweek", label: "Next week" },
  { id: "month", label: "This month" },
  { id: "custom", label: "Custom" },
];

const prettyRangeDay = (key) =>
  key ? moment(key, "YYYY-MM-DD").format("ddd, MMM D") : "";

const toDateKey = (v) => {
  if (!v) return "";

  // Firestore Timestamp
  if (typeof v === "object" && v?.seconds) {
    return moment(v.seconds * 1000).format("YYYY-MM-DD");
  }

  // strings: keep only YYYY-MM-DD
  if (typeof v === "string") {
    const s = v.slice(0, 10);
    return moment(s, "YYYY-MM-DD", true).isValid() ? s : "";
  }

  // Date
  const m = moment(v);
  return m.isValid() ? m.format("YYYY-MM-DD") : "";
};

const todayKey = () => moment().format("YYYY-MM-DD");
const tomorrowKey = () => moment().add(1, "day").format("YYYY-MM-DD");
const endOfWeekKey = () => moment().endOf("week").format("YYYY-MM-DD");
const startOfNextWeekKey = () =>
  moment().add(1, "week").startOf("week").format("YYYY-MM-DD");
const endOfNextWeekKey = () =>
  moment().add(1, "week").endOf("week").format("YYYY-MM-DD");


const getRecurrenceType = (job) => {
  let type =
    typeof job?.recurrence === "object"
      ? job?.recurrence?.type
      : job?.recurrence ??
        job?.reccurance ??
        job?.recurring ??
        job?.repeat ??
        "none";

  type = String(type || "none").toLowerCase().trim();

  if (!type || type === "one-time" || type === "onetime") return "none";
  if (type === "bi-weekly" || type === "bi weekly") return "biweekly";
  if (type === "customdates" || type === "custom dates") return "customDates";

  return type;
};

const isRecurringJob = (job) => getRecurrenceType(job) !== "none";

// This identifies WHICH OCCURRENCE a job object represents, so it must stay
// anchored to occurrenceDate/startDate, not __renderDate. __renderDate can
// differ from occurrenceDate for the same occurrence (e.g. a spillover
// render day), and this key is used both to WRITE completedDates and to
// check isOccurrenceCompleted - if it drifted to __renderDate, completing
// a job could write a different date than the one being checked, leaving
// the job stuck as "not completed" even after being marked done.
const getOccurrenceDateKey = (job) => getRecurringOccurrenceKey(job);

// Keep one rendered tile/result for each recurring WO occurrence.
// This prevents the same series occurrence from appearing more than once
// when it is returned by both the normal recurrence and override paths.
const dedupeRenderedJobs = (renderedJobs = []) => {
  const seen = new Map();

  renderedJobs.forEach((job) => {
    const occurrenceKey = getOccurrenceDateKey(job) || "UNSCHEDULED";
    const recurringKey = isRecurringJob(job)
      ? String(job.wo || job.id || "job").trim().toLowerCase()
      : String(job.id || job.wo || "job").trim().toLowerCase();
    const renderKey = toDateKey(job.__renderDate || occurrenceKey) || occurrenceKey;
    const key = `${recurringKey}_${occurrenceKey}_${renderKey}`;

    if (!seen.has(key)) seen.set(key, job);
  });

  return Array.from(seen.values());
};

const normalizeWeekday = (value) => {
  if (typeof value === "number" && value >= 0 && value <= 6) return value;

  const raw = String(value ?? "").trim().toLowerCase();
  if (/^[0-6]$/.test(raw)) return Number(raw);

  const names = {
    sunday: 0,
    sun: 0,
    monday: 1,
    mon: 1,
    tuesday: 2,
    tue: 2,
    tues: 2,
    wednesday: 3,
    wed: 3,
    thursday: 4,
    thu: 4,
    thur: 4,
    thurs: 4,
    friday: 5,
    fri: 5,
    saturday: 6,
    sat: 6,
  };

  return Object.prototype.hasOwnProperty.call(names, raw) ? names[raw] : null;
};

const getRecurringDaysOfWeek = (job, seriesStart) => {
  const recurrence =
    typeof job?.recurrence === "object" ? job.recurrence : {};

  const rawDays =
    recurrence.daysOfWeek ||
    recurrence.weekDays ||
    recurrence.weekdays ||
    job.daysOfWeek ||
    job.weekDays ||
    job.weekdays ||
    [];

  const normalized = (Array.isArray(rawDays) ? rawDays : [rawDays])
    .map(normalizeWeekday)
    .filter((day) => day !== null);

  if (normalized.length) return Array.from(new Set(normalized));

  return [seriesStart.day()];
};

const getHomeOccurrencesForJob = (job, rangeStart, rangeEnd) => {
  const recurrenceType = getRecurrenceType(job);
  const recurrence =
    typeof job?.recurrence === "object" ? job.recurrence : {};

  const seriesStart = moment(
    toDateKey(job.startDate),
    "YYYY-MM-DD",
    true
  ).startOf("day");

  if (!seriesStart.isValid()) return [];

  const windowStart = moment(rangeStart).startOf("day");
  const requestedEnd = moment(rangeEnd).startOf("day");
  const recurrenceEnd = toDateKey(
    recurrence.endsOn || recurrence.endDate || job.recurrenceEndDate
  );

  const hardEnd = recurrenceEnd
    ? moment.min(
        requestedEnd,
        moment(recurrenceEnd, "YYYY-MM-DD", true).startOf("day")
      )
    : requestedEnd;

  if (hardEnd.isBefore(seriesStart, "day")) return [];

  const dates = new Set();
  const addDate = (date) => {
    if (!date.isValid()) return;
    if (date.isBefore(seriesStart, "day")) return;
    if (date.isBefore(windowStart, "day")) return;
    if (date.isAfter(hardEnd, "day")) return;
    dates.add(date.format("YYYY-MM-DD"));
  };

  if (recurrenceType === "customDates") {
    const customDates =
      recurrence.customDates ||
      job.customDates ||
      [];

    (Array.isArray(customDates) ? customDates : [customDates]).forEach((value) => {
      const key = toDateKey(value);
      if (key) addDate(moment(key, "YYYY-MM-DD", true));
    });

    return Array.from(dates).sort();
  }

  if (recurrenceType === "daily") {
    let cursor = moment.max(seriesStart, windowStart).clone();

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      addDate(cursor);
      cursor.add(1, "day");
    }

    return Array.from(dates).sort();
  }

  if (recurrenceType === "weekly" || recurrenceType === "biweekly") {
    const selectedDays = getRecurringDaysOfWeek(job, seriesStart);
    const intervalWeeks = recurrenceType === "biweekly" ? 2 : 1;
    let cursor = moment.max(seriesStart, windowStart).clone();

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      const weeksFromStart = cursor
        .clone()
        .startOf("week")
        .diff(seriesStart.clone().startOf("week"), "weeks");

      if (
        weeksFromStart >= 0 &&
        weeksFromStart % intervalWeeks === 0 &&
        selectedDays.includes(cursor.day())
      ) {
        addDate(cursor);
      }

      cursor.add(1, "day");
    }

    return Array.from(dates).sort();
  }

  const monthIntervals = {
    monthly: 1,
    bimonthly: 2,
    quarterly: 3,
    biyearly: 6,
    yearly: 12,
  };

  if (monthIntervals[recurrenceType]) {
    let cursor = seriesStart.clone();
    while (cursor.isBefore(windowStart, "day")) {
      cursor.add(monthIntervals[recurrenceType], "months");
    }

    while (cursor.isSameOrBefore(hardEnd, "day")) {
      addDate(cursor);
      cursor.add(monthIntervals[recurrenceType], "months");
    }

    return Array.from(dates).sort();
  }

  // Safe fallback for an unknown recurring type.
  addDate(seriesStart);
  return Array.from(dates).sort();
};

const expandJobsForHome = (jobs) => {
  const outMap = new Map(); // jobId + date → job

  jobs.forEach((job) => {
  
    // ---------- NON-RECURRING ----------
if (!isRecurringJob(job)) {
  // ✅ hide Completed one-time jobs from Home list entirely
  if (job.status === "Completed" || job.status === "Invoice Sent") return;

  const k = toDateKey(job.startDate);

  // ✅ NEW: include unscheduled jobs so "Needs Scheduling" can show them
  if (!k) {
    const key = `${job.id}_UNSCHEDULED`;
    if (!outMap.has(key)) {
      outMap.set(key, {
        ...job,
        startDate: "",        // keep blank so toDateKey() stays ""
        occurrenceDate: "",   // also blank
      });
    }
    return;
  }

  const key = `${job.id}_${k}`;
  if (!outMap.has(key)) {
    outMap.set(key, {
      ...job,
      startDate: k,
      occurrenceDate: k,
    });
  }
  return;
}

   // ---------- RECURRING ----------
// NOTE: recurring series are intentionally NOT hidden based on job.status
// here. That field lives on the shared master doc, and invoicing a single
// occurrence (see SendInvoices.js handleInvoiceSent) sets it to
// "Invoice Sent" for the whole series — which would wipe every future
// occurrence off Home. Calendar/EmbeddedCalendar don't gate recurring
// visibility on this field either; they rely on per-occurrence
// completedDates/invoicedDates instead, which is what should decide
// whether a specific occurrence is closed.

const startKey = toDateKey(job.startDate);

// ✅ NEW: include recurring jobs with missing startDate so they can be scheduled
if (!startKey) {
  const key = `${job.id}_UNSCHEDULED`;
  if (!outMap.has(key)) {
    outMap.set(key, {
      ...job,
      startDate: "",
      occurrenceDate: "",
    });
  }
  return;
}


    const seriesStart = moment(startKey, "YYYY-MM-DD");
    const end = moment(endOfNextWeekKey(), "YYYY-MM-DD");

    // Generate through the current week, but do not show every historical
    // recurring occurrence as overdue. We keep only the latest open past
    // occurrence plus every open occurrence from today through week-end.
    const rawDates = getHomeOccurrencesForJob(job, seriesStart, end) || [];

    const uniq = new Set(rawDates.map((d) => toDateKey(d)).filter(Boolean));

    const excludedSet = new Set(
      (job.excludedDates || []).map(d => toDateKey(d)).filter(Boolean)
    );

    // ✅ THIS is what you were missing: skip completed occurrences
    const completedSet = new Set(
      (job.completedDates || []).map(d => toDateKey(d)).filter(Boolean)
    );

    const openDates = Array.from(uniq)
      .filter((k) => !excludedSet.has(k) && !completedSet.has(k))
      .sort();

    const today = todayKey();
    const latestPastDate =
      openDates.filter((k) => k < today).slice(-1)[0] || "";

    openDates.forEach((k) => {
      // Recurring series should contribute only its latest missed occurrence
      // to Overdue. Current and upcoming occurrences remain available for
      // Due Today, Due Tomorrow, and Due This Week.
      if (k < today && k !== latestPastDate) return;

      const key = `${job.id}_${k}`;
      if (!outMap.has(key)) {
        outMap.set(key, {
          ...job,
          startDate: k,
          endDate: k,
          occurrenceDate: k,
          originalOccurrenceDate: k,
        });
      }
    });
  });

  return Array.from(outMap.values());
};


const getDurationMismatch = (job) => {
  if (!job.startDate || !job.endDate || !job.jobPlan?.estimatedDays) return null;

  const scheduledDays =
    moment(job.endDate).diff(moment(job.startDate), "days") + 1;

  if (scheduledDays !== job.jobPlan.estimatedDays) {
    return {
      scheduledDays,
      estimatedDays: job.jobPlan.estimatedDays,
    };
  }

  return null;
};

const expandJobsForCalendar = (jobs) => {
  const outMap = new Map();
  const calendarEnd = moment().add(12, "months").endOf("day");

  jobs.forEach((job) => {
    // ---------- NON-RECURRING ----------
    if (!isRecurringJob(job)) {
      const k = toDateKey(job.startDate);
      if (!k) return;

      const key = `${job.id}_${k}`;
      outMap.set(key, {
        ...job,
        startDate: k,
        occurrenceDate: k,
      });
      return;
    }

    // ---------- RECURRING ----------
    const startKey = toDateKey(job.startDate);
    if (!startKey) return;

    const start = moment(startKey, "YYYY-MM-DD");
    const rawDates = getOccurrencesForJob(job, start, calendarEnd) || [];

    const excludedSet = new Set(
      (job.excludedDates || []).map((d) => toDateKey(d)).filter(Boolean)
    );

    const completedSet = new Set(
      (job.completedDates || []).map((d) => toDateKey(d)).filter(Boolean)
    );

    rawDates.forEach((d) => {
      const k = toDateKey(d);
      if (!k) return;

      // still hide only cancelled/excluded occurrences
      if (excludedSet.has(k)) return;

      const key = `${job.id}_${k}`;
      outMap.set(key, {
        ...job,
        startDate: k,
        endDate: k,
        occurrenceDate: k,
        originalOccurrenceDate: k,

        // mark this rendered occurrence as completed if its date is in completedDates
        occurrenceStatus: completedSet.has(k) ? "Completed" : job.status,
      });
    });
  });

  return Array.from(outMap.values());
};

const parseFormattedJobDescription = (description) =>
    // Shared reader: ((b)) bold, ((i)) italic, ((r)) regular, ((-)) bullet.
    renderFormattedText(description, { untagged: "plain" });

const getJobStartKey = (job) =>
  toDateKey(job.occurrenceDate || job.startDate);

const getJobEndKey = (job) =>
  toDateKey(job.occurrenceDate || job.endDate || job.startDate);

const jobSpansDate = (job, targetKey) => {
  const startKey = getJobStartKey(job);
  const endKey = getJobEndKey(job);

  if (!startKey) return false;
  if (!endKey) return startKey === targetKey;

  return targetKey >= startKey && targetKey <= endKey;
};

const jobSpansRange = (job, rangeStartKey, rangeEndKey) => {
  const startKey = getJobStartKey(job);
  const endKey = getJobEndKey(job);

  if (!startKey) return false;

  const safeEndKey = endKey || startKey;

  return startKey <= rangeEndKey && safeEndKey >= rangeStartKey;
};

const getTeamConversationKey = (msg) => {
  if (msg?.audience === "all") return "team_all";

  const ids = Array.from(
    new Set([msg.authorId, ...(msg.recipientIds || [])].filter(Boolean))
  ).sort();

  return `dm_${ids.join("_")}`;
};

const getTeamConversationLabel = (msg, currentUserId) => {
  if (msg?.audience === "all") return "Team All";

  const recipientNames = Array.isArray(msg?.recipientNames) ? msg.recipientNames : [];
  const authorName = msg?.authorName || "User";

  const otherNames = [];

  if (msg.authorId && msg.authorId !== currentUserId) {
    otherNames.push(authorName);
  }

  recipientNames.forEach((name, idx) => {
    const rid = msg.recipientIds?.[idx];
    if (rid && rid !== currentUserId && !otherNames.includes(name)) {
      otherNames.push(name);
    }
  });

  if (otherNames.length === 0) return "Direct Message";
  if (otherNames.length === 1) return otherNames[0];
  return `Group: ${otherNames.join(", ")}`;
};

const toJsDateSafe = (value) => {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  if (value instanceof Date) return value;
  return null;
};

// Display labels for recurrence.type values (values themselves never change).
const RECURRENCE_LABELS = {
  none: "One-Time",
  daily: "Daily",
  weekly: "Weekly",
  biweekly: "Bi-Weekly",
  monthly: "Monthly",
  bimonthly: "Bi-Monthly",
  quarterly: "Quarterly",
  biyearly: "Bi-Yearly",
  yearly: "Yearly",
  customDates: "Custom Dates",
};

const Home = () => {
  // Service Type options from Settings -> Service Types.
  const { serviceTypes } = useServiceTypes();
  const [jobs, setJobs] = useState([]);
  const [userRole, setUserRole] = useState(null);
  // The signed-in person's own role (differs from userRole during "View as").
  const [realUserRole, setRealUserRole] = useState(null);
  const [showViewAsPicker, setShowViewAsPicker] = useState(false);
  const [userId, setUserId] = useState(null);
  const [userFullName, setUserFullName] = useState("");
  const [selectedJob, setSelectedJob] = useState(null);
  const [showHistoryPopup, setShowHistoryPopup] = useState(false);
  const [showNotePopup, setShowNotePopup] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [showVerificationPopup, setShowVerificationPopup] = useState(false);
  const [usersList, setUsersList] = useState([]);
  // Honors the Show In Dropdowns flag from the Staff Settings page.
  const dropdownUsers = useMemo(() => filterStaffForDropdowns(usersList), [usersList]);
  const [jobTotals, setJobTotals] = useState({});

  /* Admin pipeline panel. Collapsed by default - this is revenue, and it
     should not be sitting open on a shared screen by accident. */
  const [pipelineOpen, setPipelineOpen] = useState(false);
  const [pipelineRange, setPipelineRange] = useState("week");
  const [pipelineFrom, setPipelineFrom] = useState(() => moment().format("YYYY-MM-DD"));
  const [pipelineTo, setPipelineTo] = useState(() => moment().add(7, "days").format("YYYY-MM-DD"));
  const [pipelineBreakdown, setPipelineBreakdown] = useState(false);
  // contractId -> amount already invoiced (deposit / progress / final), read
  // only while the Pipeline panel is open.
  const [contractBilled, setContractBilled] = useState({});
const [showTotalPopup, setShowTotalPopup] = useState(false);
const [totalInput, setTotalInput] = useState("");
// Set when the job's total lives in Job Money: Change Total then corrects
// the ORIGINAL total there (with a reason) instead of the job's own total.
const [totalLedger, setTotalLedger] = useState(null);
const [totalReason, setTotalReason] = useState("");
const [techLocation, setTechLocation] = useState(null);
const [selectedTech, setSelectedTech] = useState("ALL_TECHS");
const { t, lang } = useLanguage();
// Job tile date, e.g. "Mon, Oct 19" / "lun, 19 oct". Reads YYYY-MM-DD as a
// local date so it never shifts a day; anything else is shown as stored.
const formatTileDate = (value) => {
  const key = toDateKey(value);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key || "");
  if (!m) return key || "";
  return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString(localeFor(lang), {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};
const [showMoreMenu, setShowMoreMenu] = useState(false);
const [showGuide, setShowGuide] = useState(false); // "How do I…?" guide
const navigate = useNavigate();   // ✅ ADD THIS
// ===== Materials Popup =====
const [showMaterialsPopup, setShowMaterialsPopup] = useState(false);
const [materialsDraft, setMaterialsDraft] = useState([]);
const [jobMaterials, setJobMaterials] = useState({});
const getMaterialsForJob = (job) =>
  jobMaterials[getMaterialsGroupId(job)] || [];
const [materialsWeeksAhead, setMaterialsWeeksAhead] = useState(1); // 1 or 4
const [showMaterialsNeeded, setShowMaterialsNeeded] = useState(false); // ✅ default hidden
const [showTeamMessaging, setShowTeamMessaging] = useState(false);
const [teamHasUnread, setTeamHasUnread] = useState(false);
const [teamLatestActivityAt, setTeamLatestActivityAt] = useState(null);
const [teamLastSeenAt, setTeamLastSeenAt] = useState(null);
const [teamOpenTaskCount, setTeamOpenTaskCount] = useState(0);
const [teamUnreadLabels, setTeamUnreadLabels] = useState([]);
const [currentUserData, setCurrentUserData] = useState(null);
const [selectedAvatarFile, setSelectedAvatarFile] = useState(null);
const [uploadingAvatar, setUploadingAvatar] = useState(false);

const [showContinuePopup, setShowContinuePopup] = useState(false);
// "Duplicate" (Supervisor section): copy under the next WO, original stays Active.
const [duplicateJob, setDuplicateJob] = useState(null);
// Job that was just completed and still needs its invoice type picked.
const [billingPromptJob, setBillingPromptJob] = useState(null);
// "loading" | "ready" | "error" - drives the load banner / Try again button.
const [homeLoadState, setHomeLoadState] = useState("loading");
const [homeReloadKey, setHomeReloadKey] = useState(0);
// Job just created by Continue: ask to confirm its location (job fence).
const [fencePromptJob, setFencePromptJob] = useState(null);
const [continueDate, setContinueDate] = useState("");
const [continuingJob, setContinuingJob] = useState(false);

const [showReceiptPopup, setShowReceiptPopup] = useState(false);
const [receiptAmount, setReceiptAmount] = useState("");
const [receiptFile, setReceiptFile] = useState(null);
const [receiptNote, setReceiptNote] = useState("");
const [savingReceipt, setSavingReceipt] = useState(false);

const isAdmin = userRole === "Admin";

const [selectedExportJobIds, setSelectedExportJobIds] = useState([]);

const [techDateView, setTechDateView] = useState("today");
// "today" | "tomorrow" | "week"
const expandedHomeJobs = useMemo(() => {
  const normalOccurrences = expandJobsForHome(jobs).map(
    applyOccurrenceOverrideToRenderedJob
  );

  const movedOccurrences = jobs
    .flatMap(getOccurrenceOverrideRenderJobs)
    .filter((job) => {
      const key = toDateKey(job.__renderDate || job.startDate);
      return key && key <= endOfNextWeekKey();
    });

  return dedupeRenderedJobs(
    mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ])
  );
}, [jobs]);

// 🚨 Missed jobs: date passed, never completed (utils/missedJobs.js).
// Same rows drive the Missed chip, the red banner and the popup. Built from
// the jobs Home already loaded - the only extra read is the small live list
// of pending tech completions (those are done, not missed).
const pendingCompletionIds = usePendingCompletionIds(
  ["Admin", "Manager", "Assistant"].includes(userRole)
);
const missedRows = useMemo(
  () => buildMissedJobRows(jobs, pendingCompletionIds),
  [jobs, pendingCompletionIds]
);
const missedKeySet = useMemo(
  () => new Set(missedRows.map((row) => row.key)),
  [missedRows]
);
const isMissedRenderedJob = (job) => missedKeySet.has(missedKeyForRenderedJob(job));

const expandedCalendarJobs = useMemo(() => {
  const normalOccurrences = expandJobsForCalendar(jobs).map(
    applyOccurrenceOverrideToRenderedJob
  );

  const movedOccurrences = jobs.flatMap(getOccurrenceOverrideRenderJobs);

  return dedupeRenderedJobs(
    mergeRenderedRecurringJobs([
      ...normalOccurrences,
      ...movedOccurrences,
    ])
  );
}, [jobs]);

// 🧱 Materials needed this week (STAFF ONLY)
const weeklyMaterials = useMemo(() => {
  return materialsNeededForWeeks(jobs, materialsWeeksAhead, jobMaterials);
}, [jobs, materialsWeeksAhead, jobMaterials]);

const functions = getFunctions(app, "us-central1");
const sendJobNoteEmailCallable = httpsCallable(functions, "sendJobNoteEmail");


const LOCATION_OPTIONS = ["Shop 1", "Property", "Van 1", "Van 2", "Truck 1", "Trailer 1"];

const [openReceivedKey, setOpenReceivedKey] = useState(null); 
// string like `${jobId}_${materialIndex}`

const [receivedLocationDraft, setReceivedLocationDraft] = useState({});
// { "jobId_idx": "Shop 1" }

const HOME_ACTIVE_TAB_STORAGE_KEY = "servicing_home_active_tab";
const VALID_HOME_TABS = [
  "jobs",
  "clock",
  "job-calendar",
  "time-log-calendar",
  "customer-codes",
  "maps",
  "time-off",
  "clock-time",
  "warehouse",
];

const [activeTab, setActiveTab] = useState(() => {
  const linked = readLinkParams().go; // ?go=<tab> from a guide link
  if (VALID_HOME_TABS.includes(linked)) return linked;
  try {
    const stored = sessionStorage.getItem(HOME_ACTIVE_TAB_STORAGE_KEY);
    return VALID_HOME_TABS.includes(stored) ? stored : "jobs";
  } catch (e) {
    return "jobs";
  }
});
// jobs | clock | job-calendar | time-log-calendar | customer-codes | maps | time-off | clock-time

useLinkedTab(({ go }) => {
  if (VALID_HOME_TABS.includes(go)) setActiveTab(go);
});

useEffect(() => {
  try {
    sessionStorage.setItem(HOME_ACTIVE_TAB_STORAGE_KEY, activeTab);
  } catch (e) {
    // sessionStorage unavailable (e.g. private browsing) — safe to ignore
  }
}, [activeTab]);

const [dateFilter, setDateFilter] = useState(""); // for summary strip filtering
const summary = useMemo(() => {
  const counts = {
    allActive: 0,
    overdue: 0,
    dueToday: 0,
    dueTomorrow: 0,
    dueThisWeek: 0,
    dueNextWeek: 0,
    needsScheduling: 0,
  };

  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  expandedHomeJobs.forEach((job) => {
    counts.allActive++;

    const startKey = getJobStartKey(job);
    if (!startKey) {
      counts.needsScheduling++;
      return;
    }

    if (jobSpansDate(job, tKey)) counts.dueToday++;
    if (jobSpansDate(job, tmKey)) counts.dueTomorrow++;
    if (jobSpansRange(job, tmKey, eowKey)) counts.dueThisWeek++;
    if (jobSpansRange(job, nextWeekStartKey, nextWeekEndKey)) counts.dueNextWeek++;
  });

  // Overdue = missed: the job's LAST day is before today and it was never
  // completed (see utils/missedJobs.js). This used to count any job that
  // STARTED before today, so multi-day jobs still in progress were counted
  // as overdue too.
  counts.overdue = missedRows.length;

  return counts;
}, [expandedHomeJobs, missedRows]);

const isOccurrenceCompleted = (job) => isOccurrenceCompletedFor(job);

const getJobNotesForDisplay = (job) => {
  if (!job) return [];

  if (isRecurringJob(job)) {
    const occurrenceKey = getOccurrenceDateKey(job);
    return job.occurrenceNotes?.[occurrenceKey] || [];
  }

  return Array.isArray(job.notes) ? job.notes : [];
};

// Supervisor notes: JobHistory shows the full running list for the job
// (unchanged there), but on a specific occurrence's tile/detail view we
// only want the notes added for THAT occurrence. Notes added before this
// scoping existed have no occurrenceDate tag and are treated as
// belonging to no single occurrence (they stay visible in JobHistory).
const getSupervisorNotesForDisplay = (job) => {
  if (!job) return [];

  const allNotes = Array.isArray(job.supervisorNotes) ? job.supervisorNotes : [];

  if (!isRecurringJob(job)) return allNotes;

  const occurrenceKey = getOccurrenceDateKey(job);
  return allNotes.filter((note) => note?.occurrenceDate === occurrenceKey);
};

const completedTileStyle = {
  background: "#9ca3af",   // gray-400
  color: "#111827",
};


// ⏱️ Clock-in logic (SINGLE SOURCE OF TRUTH)
const clock = useClockInLogic();
// Job check-in / check-out (job_visits). Lives inside the clocked-in day.
const jobVisits = useJobVisits(clock);

const getTeamMessagingButtonStyle = () => {
  let bg = "#e0e7ff";

  if (teamHasUnread || teamOpenTaskCount > 0) {
    bg = "#fecaca";
  } else if (showTeamMessaging || teamLastSeenAt) {
    bg = "#93c5fd";
  }

  return {
    padding: "8px 14px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: bg,
    color: "#0f172a",
    fontWeight: 900,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    position: "relative",
  };
};

const chip = (bgColor, active = false) => ({
  padding: "6px 12px",
  borderRadius: "16px",
  background: bgColor,
  cursor: "pointer",
  fontWeight: 600,
  border: active ? "2px solid #1d4ed8" : "1px solid transparent",
});

const getJobBucket = (job) => {
  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  const startKey = getJobStartKey(job);

  if (!startKey) return "needsScheduling";
  if (jobSpansDate(job, tKey)) return "today";
  if (jobSpansDate(job, tmKey)) return "tomorrow";
  if (jobSpansRange(job, tmKey, eowKey)) return "week";
  if (jobSpansRange(job, nextWeekStartKey, nextWeekEndKey)) return "nextWeek";
  if (isMissedRenderedJob(job)) return "overdue";

  return null;
};

const jobRowStyle = (job) => {
  const bucket = getJobBucket(job);

  const colorMap = {
    overdue: "#ffebee",
    today: "#e0f2fe",
    tomorrow: "#fff3cd",
    week: "#e3f2fd",
    nextWeek: "#dbeafe",
    needsScheduling: "#f3e5f5",
  };  

  const bg = bucket ? colorMap[bucket] : "white";

  return {
    border: "1px solid #1d4ed8",
    borderRadius: 8,
    padding: 10,
    marginBottom: 8,
    cursor: "pointer",
    background: bg,
  };
};


const myJobs = useMemo(() => {
  if (!userFullName) return [];

  return expandedHomeJobs.filter((job) => {
    if (Array.isArray(job.techAssign)) {
      return job.techAssign.includes(userFullName);
    }
    return job.techAssign === userFullName;
  });
}, [expandedHomeJobs, userFullName]);

// Automatic check-in / check-out for this person's jobs that have a
// confirmed location (job fence). Jobs without one are untouched.
const fenceAuto = useJobFenceAuto({ clock, jobVisits, jobs: myJobs });

// Keep the job list and the open popup in step after a location is confirmed.
const applyJobFence = (jobId, jobFence) => {
  setJobs((prev) => prev.map((j) => (j.id === jobId ? { ...j, jobFence } : j)));
  setSelectedJob((prev) => (prev && prev.id === jobId ? { ...prev, jobFence } : prev));
};

const matchesTechDateView = (job) => {
  const k = toDateKey(job.startDate);
  if (!k) return false;

  switch (techDateView) {
    case "today":
      return k === todayKey();
    case "tomorrow":
      return k === tomorrowKey();
    case "week":
      return k >= todayKey() && k <= endOfWeekKey();
    default:
      return false;
  }
};

const techUpcomingJobs = useMemo(() => {
  return myJobs.filter(matchesTechDateView);
}, [myJobs, techDateView]);

const techJobsTitle = useMemo(() => {
  switch (techDateView) {
    case "today":
      return t("Today’s Jobs");
    case "tomorrow":
      return t("Tomorrow’s Jobs");
    case "week":
      return t("This Week’s Jobs");
    default:
      return t("Jobs");
  }
}, [techDateView, t]);

// One-time: status === "Completed". Recurring: only if THIS occurrence's
// original date is in completedDates. Both handled by the shared helper.
const isJobCompleted = (job) => isOccurrenceCompletedFor(job);



  // 🔍 search + filter + sort
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredJobs, setFilteredJobs] = useState([]);
  const [companyWorkOrderSearch, setCompanyWorkOrderSearch] = useState(""); // kept for future use
  const [clientWorkOrderSearch, setClientWorkOrderSearch] = useState(""); // kept for future use

  // NEW: site filter + sort dropdown (string-based)
  const [selectedSite, setSelectedSite] = useState("ALL_SITES");
  const [sortOption, setSortOption] = useState(""); // '', 'siteName', 'startDateAsc', 'startDateDesc', 'cityAsc', 'cityDesc'

  // NEW: filter by specific start date
  const [selectedStartDate, setSelectedStartDate] = useState(""); // "YYYY-MM-DD"

  const [isEditing, setIsEditing] = useState(false);
  const [editedJob, setEditedJob] = useState({});
  // Job close/delete now routes through the shared closeOrDeleteJob scope
  // dialog (see handleDeleteJob) instead of a separate confirm popup.
  const isTech = userRole === "Tech";
  // Office: always. Techs: per Staff Settings (everyone, or picked people).
  const checklistAccess = useChecklistAccess(userRole);
  // Tech "Mark as Completed" goes to the office's Ready to Invoice panel
  // instead of changing the job (see utils/techCompletion.js).
  const {
    request: techCompletionRequest,
    setRequest: setTechCompletionRequest,
  } = useTechCompletionRequest(isTech ? selectedJob : null);
// True when the signed-in person is on this job's techAssign list.
const isAssignedToMe = (job) => {
  if (!job || !userFullName) return false;
  const ta = job.techAssign;
  return Array.isArray(ta) ? ta.includes(userFullName) : ta === userFullName;
};
// Admin Portal access (ONLY Admin / Manager)
const canAccessAdminPortal = ["Admin", "Manager"].includes(userRole);

// Staff = Admin + Manager + Assistant
const isStaff = ["Admin", "Manager", "Assistant"].includes(userRole);

// Code tools = Admin + Manager + Assistant + Tech
const canUseCodes = ["Admin", "Manager", "Assistant", "Tech"].includes(userRole);

const canSeeSupervisorNotes = ["Admin", "Manager", "Assistant"].includes(userRole);


// Destructive actions
const canDelete = ["Admin", "Manager"].includes(userRole);

// Proposal deposits that have money against them but no job yet (paid by
// Stripe link, bulk upload, Bank Statement entry, or an attempt that failed)
// get their Active job the moment office staff open the app. Once per tab.
useEffect(() => {
  if (!isStaff || isViewingAs()) return;
  if (window.__depositJobSweepDone) return;
  window.__depositJobSweepDone = true;
  (async () => {
    try {
      const created = await ensureJobsForDepositPayments();
      if (!created.length) return;
      setHomeReloadKey((k) => k + 1);
      alert(
        `Deposit received. New active job${created.length > 1 ? "s" : ""} created:\n` +
          created.map((c) => `${c.depositWO} -> ${c.wo}`).join("\n")
      );
    } catch (err) {
      window.__depositJobSweepDone = false;
      console.error("Deposit job check failed:", err);
    }
  })();
}, [isStaff]);

  

const location = useLocation();

useEffect(() => {
  const reopenJobId = location.state?.reopenJobId;
  if (!reopenJobId || !jobs.length) return;

  const occurrenceDate = location.state?.reopenOccurrenceDate || "";
  const match = jobs.find((job) => {
    if (job.id !== reopenJobId) return false;
    if (!occurrenceDate) return true;
    return (job.occurrenceDate || job.startDate || "") === occurrenceDate || job.id === reopenJobId;
  });

  if (match) setSelectedJob(match);
}, [location.state, jobs]);

// ================= 🚨 MISSED JOBS POPUP (staff only) =================
// Opens by itself once when a staff member lands on Home and there are
// missed jobs that were not put off until tomorrow. "Remind me tomorrow" /
// closing hides it for the rest of the day (per person, this browser). The
// red banner above the jobs opens it any time.
const [missedOpen, setMissedOpen] = useState(false);
const [missedSnooze, setMissedSnooze] = useState({ date: "", all: false, keys: [] });
const missedAutoShownRef = useRef(false);

useEffect(() => {
  if (missedAutoShownRef.current) return;
  if (!isStaff || isViewingAs() || homeLoadState !== "ready" || !userId) return;

  const snooze = readSnooze(userId);
  setMissedSnooze(snooze);

  if (snooze.all) {
    missedAutoShownRef.current = true;
    return;
  }

  const snoozedKeys = new Set(snooze.keys);
  if (missedRows.some((row) => !snoozedKeys.has(row.key))) {
    missedAutoShownRef.current = true;
    setMissedOpen(true);
  }
}, [isStaff, homeLoadState, userId, missedRows]);

const openMissedJobs = () => {
  setMissedSnooze(readSnooze(userId));
  setMissedOpen(true);
};

const remindMissedLater = () => {
  setMissedSnooze(snoozeAllForToday(userId));
  setMissedOpen(false);
};

const snoozeMissedKeys = (keys) => {
  setMissedSnooze(snoozeKeysForToday(userId, keys));
};

const applyMissedResults = (results) => {
  setJobs((prev) => applyMissedPatches(prev, results));
};

const openMissedJob = (row) => {
  setMissedOpen(false);
  setIsEditing(false);
  setSelectedJob(row.renderJob);
};

// Needs Scheduling -> Missed tab -> "Open" lands here.
const handledMissedOpenRef = useRef("");
useEffect(() => {
  const key = location.state?.openMissedRowKey;
  if (!key || handledMissedOpenRef.current === key) return;
  const row = missedRows.find((r) => r.key === key);
  if (!row) return;
  handledMissedOpenRef.current = key;
  missedAutoShownRef.current = true; // they came for this job, not the popup
  setIsEditing(false);
  setSelectedJob(row.renderJob);
}, [location.state, missedRows]);

// ================= 👷 ⏱ 💵 JOB REMINDERS (staff only) =================
// Same idea as Missed Jobs, one popup + banner each:
//   noTech     jobs (14 days back to 7 ahead) with nobody on Tech Assign
//   clockedIn  last 7 days: clocked in but not on any job that day
//   subPay     last 60 days: a sub on a job with no pay set (Admin only)
// Only one popup shows at a time, after Missed Jobs is closed. "Remind me
// tomorrow" hides that one for the day. Dismiss is permanent (shared).
// Every job, any date: Job History -> Reminders.
// New proposal jobs first: they have no date yet, so nothing else surfaces them.
const REMINDER_ORDER = ["proposalJob", "dupWO", "noTech", "clockedIn", "subPay"];
const reminderEnabled = isStaff && !isViewingAs() && homeLoadState === "ready" && Boolean(userId);
const homeReminders = useJobReminders({
  jobs,
  users: usersList,
  enabled: reminderEnabled,
  userRole,
  scope: "home",
});
const reminderStaffNames = useMemo(
  () =>
    filterStaffForDropdowns(usersList || [])
      .map((u) => `${u.firstName || ""} ${u.lastName || ""}`.trim())
      .filter(Boolean)
      .sort((a, b) => a.localeCompare(b)),
  [usersList]
);
const [reminderOpen, setReminderOpen] = useState("");
const [reminderSnooze, setReminderSnooze] = useState({});
const reminderAutoShownRef = useRef({});

useEffect(() => {
  if (!reminderEnabled || !homeReminders.loaded) return;
  // Let Missed Jobs go first; wait while it (or another reminder) is open.
  if (missedOpen || reminderOpen) return;
  if (!missedAutoShownRef.current && missedRows.length) return;
  for (const kind of REMINDER_ORDER) {
    if (reminderAutoShownRef.current[kind]) continue;
    const rows = homeReminders.rows[kind] || [];
    const snooze = readReminderSnooze(userId, kind);
    reminderAutoShownRef.current[kind] = true;
    if (snooze.all) continue;
    const snoozed = new Set(snooze.keys);
    if (rows.some((r) => !snoozed.has(r.key))) {
      setReminderSnooze((p) => ({ ...p, [kind]: snooze }));
      setReminderOpen(kind);
      return;
    }
  }
}, [reminderEnabled, homeReminders.loaded, homeReminders.rows, missedOpen, reminderOpen, missedRows.length, userId]);

const openReminder = (kind) => {
  setReminderSnooze((p) => ({ ...p, [kind]: readReminderSnooze(userId, kind) }));
  setReminderOpen(kind);
};
const remindLater = (kind) => {
  setReminderSnooze((p) => ({ ...p, [kind]: snoozeReminderAll(userId, kind) }));
  setReminderOpen("");
};
const snoozeReminderRowKeys = (kind, keys) => {
  setReminderSnooze((p) => ({ ...p, [kind]: snoozeReminderKeys(userId, kind, keys) }));
};
const patchReminderJob = (id, patch) => {
  setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
};
const openReminderJob = (row) => {
  const job = jobs.find((j) => j.id === row.jobId) || row.job;
  if (!job) return;
  setReminderOpen("");
  setIsEditing(false);
  setSelectedJob(job);
};

// ✅ base (Home button color) + active (dark blue)
const topNavBtnStyle = (path, isProtected = false) => {
  const isActive = location.pathname === path;

  return {
    padding: "10px 14px",
    borderRadius: 10,
    border: "1px solid #1d4ed8",
    background: isActive ? "#1d4ed8" : "#e0e7ff", // ✅ default = Home color
    color: isActive ? "#fff" : "#0f172a",
    fontWeight: 800,
    cursor: "pointer",
  };
};
  
  useEffect(() => {
    if (showMaterialsPopup && selectedJob) {
      const groupId = getMaterialsGroupId(selectedJob);
      (async () => {
        const currentMaterials =
          jobMaterials[groupId] || (await loadMaterialsForJob(selectedJob));

        const normalizedMaterials = currentMaterials.map((material) => ({
          ...material,
          name: material.name || material.product || "",
          qty: Number(material.qty ?? 1),
          unit: material.unit || "pcs",
          costPer: material.costPer ?? "",
          purchaseLocation: material.purchaseLocation || "",
          notes: material.notes || "",
          purchased: Boolean(material.purchased),
          files: Array.isArray(material.files) ? material.files : [],
        }));

        setJobMaterials((previous) => ({ ...previous, [groupId]: currentMaterials }));
        setMaterialsDraft(normalizedMaterials);
      })();
    }
  }, [showMaterialsPopup, selectedJob]);
  
  useEffect(() => {
    const fetchUserData = async () => {
      const auth = getAuth();
      const currentUser = auth.currentUser;
    
      if (currentUser) {
        const usersRef = collection(db, "users");
        const querySnapshot = await withRetry(() => getDocs(usersRef));
    
        // build full users list for Tech dropdown
        const userList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsersList(userList);
    
        // find current user (same logic as before)
        const realDoc = userList.find((u) => u.id === currentUser.uid);
        setRealUserRole(realDoc?.role || null);

        // "View as" (Admin only): load Home as the chosen person. Everything
        // that saves is blocked while this is on (utils/viewAs.js).
        const va = getViewAs();
        const viewedDoc =
          va && realDoc?.role === "Admin"
            ? userList.find((u) => u.id === va.docId) ||
              userList.find((u) => (u.authUid || u.id) === va.uid)
            : null;

        const userDoc = viewedDoc || realDoc;
        setUserId(viewedDoc ? va.uid : currentUser.uid);
        if (userDoc) {
          setUserRole(userDoc.role);
          setUserFullName(`${userDoc.firstName} ${userDoc.lastName}`);
          setCurrentUserData(userDoc);
        }
      }
    }; 

    const fetchJobs = async () => {
      const jobsRef = collection(db, "jobs");
      // Jobs, totals and materials are independent - read them at the same
      // time instead of one after another.
      const [querySnapshot, totalsSnap, materialsMap] = await Promise.all([
        withRetry(() => getDocs(jobsRef)),
        withRetry(() => getDocs(collection(db, "job_totals"))).catch((error) => {
          console.error("Error fetching job totals:", error);
          return null;
        }),
        fetchAllJobMaterials().catch((error) => {
          console.error("Error fetching job materials:", error);
          return null;
        }),
      ]);
    
      const allJobs = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    
      const hiddenServiceTypes = ["Approved Proposal", "Denied Proposal"];
    
      const visibleJobs = allJobs.filter(
        (job) => !hiddenServiceTypes.includes(job.serviceType)
      );      
    
      setJobs(visibleJobs);
      const totalsMap = {};
      if (totalsSnap) {
        totalsSnap.forEach(doc => {
          totalsMap[doc.id] = doc.data().total;
        });
      }
      setJobTotals(totalsMap);

      if (materialsMap) setJobMaterials(materialsMap);
    };

    // Before, a failed read (weak signal) left Home half-drawn forever and the
    // only fix was refreshing. Now it shows "Loading..." then either the jobs
    // or a clear "Try again" button.
    let cancelled = false;
    setHomeLoadState("loading");
    Promise.all([fetchUserData(), fetchJobs()])
      .then(() => {
        if (!cancelled) setHomeLoadState("ready");
      })
      .catch((error) => {
        console.error("Home failed to load:", error);
        if (!cancelled) setHomeLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [homeReloadKey]);

  useEffect(() => {
    if (!userId) return;
  
    const seenRef = doc(db, "teamMessagingSeen", userId);
  
    let seenMap = {};
    let latestTaskAt = null;
    let latestConversationItems = [];
  
    const recompute = () => {
      const unreadConversations = latestConversationItems.filter((item) => {
        const seenAt = toJsDateSafe(seenMap[item.key]);
        return item.latestAt && (!seenAt || item.latestAt > seenAt);
      });
  
      setTeamUnreadLabels(unreadConversations.map((x) => x.label));
      setTeamHasUnread(unreadConversations.length > 0);
  
      const latest =
        [
          ...latestConversationItems.map((x) => x.latestAt),
          latestTaskAt,
        ]
          .filter(Boolean)
          .sort((a, b) => b - a)[0] || null;
  
      setTeamLatestActivityAt(latest);
    };
  
    const unsubSeen = onSnapshot(seenRef, (snap) => {
      const data = snap.exists() ? snap.data() : {};
      seenMap = data?.conversationSeen || {};
      setTeamLastSeenAt(toJsDateSafe(data?.lastSeenAt));
      recompute();
    });
  
    const unsubMessages = onSnapshot(
      // Only the newest messages matter for the unread badge. Listening to the
      // whole message history made every Home load slower as it grew.
      query(collection(db, "teamMessages"), orderBy("createdAt", "desc"), limit(400)),
      (snapshot) => {
        const docs = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((msg) => {
            if (msg.audience === "all") return true;
            if (msg.authorId === userId) return true;
            return Array.isArray(msg.recipientIds) && msg.recipientIds.includes(userId);
          });
  
        const convoMap = new Map();
  
        docs.forEach((msg) => {
          if (!msg.createdAt?.toDate) return;
          if (msg.authorId === userId) return; // unread only from others
  
          const key = getTeamConversationKey(msg);
          const label = getTeamConversationLabel(msg, userId);
          const createdAt = msg.createdAt.toDate();
  
          const current = convoMap.get(key);
          if (!current || createdAt > current.latestAt) {
            convoMap.set(key, { key, label, latestAt: createdAt });
          }
        });
  
        latestConversationItems = Array.from(convoMap.values());
        recompute();
      }
    );
  
    const unsubTasks = onSnapshot(
      query(collection(db, "teamTasks"), orderBy("createdAt", "desc")),
      (snapshot) => {
        const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  
        const openTasks = docs.filter((task) => task.status !== "Completed");
        setTeamOpenTaskCount(openTasks.length);
  
        const latestOtherTask = docs.find((task) => {
          const createdByOther =
            task.createdById &&
            task.createdById !== userId &&
            task.createdAt?.toDate;
  
          const completedByOther =
            task.completedById &&
            task.completedById !== userId &&
            task.completedAt?.toDate;
  
          return createdByOther || completedByOther;
        });
  
        const createdAt =
          latestOtherTask?.createdById &&
          latestOtherTask?.createdById !== userId &&
          latestOtherTask?.createdAt?.toDate
            ? latestOtherTask.createdAt.toDate()
            : null;
  
        const completedAt =
          latestOtherTask?.completedById &&
          latestOtherTask?.completedById !== userId &&
          latestOtherTask?.completedAt?.toDate
            ? latestOtherTask.completedAt.toDate()
            : null;
  
        latestTaskAt = [createdAt, completedAt].filter(Boolean).sort((a, b) => b - a)[0] || null;
  
        recompute();
      }
    );
  
    return () => {
      unsubSeen();
      unsubMessages();
      unsubTasks();
    };
  }, [userId]);

  // 🔽 build unique site list from jobs
  const siteOptions = useMemo(() => {
    const set = new Set();
    jobs.forEach((j) => {
      const n = (j.siteName || "").trim();
      if (n) set.add(n);
    });
    return ["ALL_SITES", ...Array.from(set).sort((a, b) => a.localeCompare(b))];
  }, [jobs]);

  useEffect(() => {
    if (activeTab !== "jobs") {
      setDateFilter("");
      setSelectedStartDate("");
    }
  }, [activeTab]);
  
  useEffect(() => {
    filterAndSortJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobs, expandedHomeJobs, missedKeySet, searchTerm, selectedSite, sortOption, selectedStartDate, dateFilter, selectedTech]);  

  const handleAvatarUpload = async (e) => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    const file = e.target.files?.[0];
    if (!file || !userId) return;
  
    try {
      setUploadingAvatar(true);
  
      const storage = getStorage();
      const avatarRef = ref(
        storage,
        `users/${userId}/avatar/${Date.now()}_${file.name}`
      );
  
      await uploadBytes(avatarRef, file);
      const avatarURL = await getDownloadURL(avatarRef);
  
      await updateDoc(doc(db, "users", userId), { avatarURL });
  
      setCurrentUserData((prev) => ({
        ...prev,
        avatarURL,
      }));
  
      setUsersList((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, avatarURL } : u
        )
      );
    } catch (error) {
      console.error("Error uploading avatar:", error);
      alert(t("Could not upload avatar."));
    } finally {
      setUploadingAvatar(false);
      e.target.value = "";
    }
  };

  const closeModal = () => {
    setSelectedJob(null);
    setIsEditing(false);
  };
  const closeNotePopup = () => setShowNotePopup(false);
  const closeVerificationPopup = () => setShowVerificationPopup(false);

  const isProjectJob = (job) => {
    return job?.isProject === true && job?.projectStatus !== "Project Completed";
  };
  
  const getNextProjectDate = (job) => {
    const baseDate = toDateKey(job?.startDate) || todayKey();
    return moment(baseDate, "YYYY-MM-DD").add(1, "day").format("YYYY-MM-DD");
  };

  // Next free branch: PJMC-3041 -> PJMC-3041-2 -> -3 ...
  // Checks EVERY job in the database (this page only loads some of them) and
  // ignores upper/lower case, so an old "pjmc-3041-2" counts as taken.
  const getContinuedWO = async (baseWO) => {
    if (!baseWO) return "";
    try {
      return await getNextContinuedWO(baseWO);
    } catch (err) {
      console.warn("Work order lookup failed; using the jobs on this page:", err);
    }

    // Fallback: the jobs this page has, same rules.
    const base = getWOStem(baseWO);
    const baseKey = base.toUpperCase();
    let highest = 1;
    jobs.forEach((j) => {
      const match = String(j.wo || "").trim().toUpperCase().match(/^(.*)-(\d+)$/);
      if (match && match[1] === baseKey) highest = Math.max(highest, Number(match[2]) || 0);
    });
    return `${base}-${highest + 1}`;
  };
  
  const continueJobToNewDate = async () => {
  
    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob?.id || !continueDate) {
      alert("Please select the new date.");
      return;
    }
  
    const oldDate = toDateKey(selectedJob.startDate);
  
    if (continueDate === oldDate) {
      alert("The continue date must be different from the original start date.");
      return;
    }
  
    setContinuingJob(true);
  
    try {
      const oldJobRef = doc(db, "jobs", selectedJob.id);
  
      const newWO = await getContinuedWO(selectedJob.wo);
  
      const copiedJob = {
        // Same strip list as the invoice pages' Continue: the new WO must not
        // arrive already invoiced / paid / continued (approvedInvoice,
        // invoiceSent, paid, continuedToWO ... used to ride along, so a
        // continued job could skip billing entirely). contractBilling stays:
        // it carries the contract total, deposit and balance to the next WO.
        ...stripForContinuation(selectedJob, { keepTotals: true }),
  
        // remove fields that should not copy as Firestore document fields
        id: undefined,
        __renderDate: undefined,
        occurrenceDate: undefined,
        newFiles: undefined,
  
        // new independent job
        wo: newWO || selectedJob.wo || "",
        startDate: continueDate,
        endDate: continueDate,
        status: "Active",
        recurrence: { type: "none" },
  
        // keep record of where it came from, but it is NOT linked for edits
        continuedFromJobId: selectedJob.id,

        // Same property, so the confirmed job location carries forward. The
        // location popup below asks if it is still right.
        ...(selectedJob.jobFence
          ? { jobFence: { ...selectedJob.jobFence, carriedFromJobId: selectedJob.id } }
          : {}),
        materialsGroupId: getMaterialsGroupId(selectedJob),
        originalStartDate: oldDate || selectedJob.originalStartDate || "",
        continuedFromDate: oldDate || "",
  
        notes: [],
        receipts: [],
        receiptsTotal: 0,
        completedDates: [],
        occurrenceNotes: {},
  
        creationDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByContinue: true,
        createdBy: {
          uid: userId || "",
          firstName: userFullName?.split(" ")?.[0] || "",
          lastName: userFullName?.split(" ")?.slice(1).join(" ") || "",
          fullName: userFullName || "User",
        },
      };
  
      if (copiedJob.jobPlan) {
        copiedJob.jobPlan = { ...copiedJob.jobPlan };
        delete copiedJob.jobPlan.materials;
      }

      Object.keys(copiedJob).forEach((key) => {
        if (copiedJob[key] === undefined) delete copiedJob[key];
      });
  
      // Sub contractor daily / project pay rolls over (project is owed once).
      Object.assign(copiedJob, await rollSubPayTermsOnto(copiedJob));

      await updateDoc(oldJobRef, {
        status: "Completed",
        completedDates: arrayUnion(oldDate || todayKey()),
        continuedToDate: continueDate,
        continuedToWO: newWO || selectedJob.wo || "",
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      const newDocRef = await addDoc(collection(db, "jobs"), copiedJob);
  
      const completedOldJob = {
        ...selectedJob,
        status: "Completed",
        completedDates: [
          ...(selectedJob.completedDates || []),
          oldDate || todayKey(),
        ],
        continuedToDate: continueDate,
        continuedToWO: newWO || selectedJob.wo || "",
      };
  
      const newJobForState = {
        ...copiedJob,
        id: newDocRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      };
  
      setJobs((prev) => [
        ...prev.map((job) =>
          job.id === selectedJob.id ? completedOldJob : job
        ),
        newJobForState,
      ]);
  
      setSelectedJob(null);
      setShowContinuePopup(false);
      setContinueDate("");
  
      alert(`Original job completed. New job scheduled for ${continueDate}.`);

      // Ask to confirm (or set) the location for the new job.
      if (isStaff) setFencePromptJob(newJobForState);

      // The original job is now Completed, so it is billed now: usually a
      // progress payment on a contract job.
      // (A project day is not billed on its own - only the project close is.)
      if (isAwaitingInvoice(completedOldJob)) setBillingPromptJob(completedOldJob);
    } catch (err) {
      console.error("Error continuing job:", err);
      alert("Could not continue this job.");
    } finally {
      setContinuingJob(false);
    }
  };

  const completeProjectDayAndCreateNext = async () => {
    if (!selectedJob?.id) return;
  
    const ok = window.confirm(
      "Complete this project day and create the next active project day?"
    );
  
    if (!ok) return;
  
    const oldDate = toDateKey(selectedJob.startDate) || todayKey();
    const nextDate = getNextProjectDate(selectedJob);
    const oldJobRef = doc(db, "jobs", selectedJob.id);
    const newWO = await getContinuedWO(selectedJob.wo);
  
    try {
      const projectRootId =
        selectedJob.projectRootId || selectedJob.continuedFromJobId || selectedJob.id;
  
      const copiedJob = {
        // Invoice / paid marks never carry to the next project day.
        ...stripForContinuation(selectedJob, { keepTotals: true }),
  
        id: undefined,
        __renderDate: undefined,
        occurrenceDate: undefined,
        newFiles: undefined,
  
        wo: newWO || selectedJob.wo || "",
        startDate: nextDate,
        endDate: nextDate,
        status: "Active",
        recurrence: { type: "none" },
  
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectDayNumber: Number(selectedJob.projectDayNumber || 1) + 1,
  
        projectContinuedFromJobId: selectedJob.id,
        projectContinuedFromDate: oldDate,
        materialsGroupId: projectRootId,
  
        // keep project history visible on each new day
        notes: Array.isArray(selectedJob.notes) ? selectedJob.notes : [],
        fileURLs: Array.isArray(selectedJob.fileURLs) ? selectedJob.fileURLs : [],
        fileNames: Array.isArray(selectedJob.fileNames) ? selectedJob.fileNames : [],
        jobPlan: (() => {
          const plan = { ...(selectedJob.jobPlan || {}) };
          delete plan.materials;
          return plan;
        })(),
  
        // keep receipts separate by day
        receipts: [],
        receiptsTotal: 0,
  
        completedDates: [],
        occurrenceNotes: {},
  
        creationDate: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdByProjectContinue: true,
        createdBy: {
          uid: userId || "",
          firstName: userFullName?.split(" ")?.[0] || "",
          lastName: userFullName?.split(" ")?.slice(1).join(" ") || "",
          fullName: userFullName || "User",
        },
      };
  
      Object.keys(copiedJob).forEach((key) => {
        if (copiedJob[key] === undefined) delete copiedJob[key];
      });
  
      // Sub contractor daily / project pay rolls over (project is owed once).
      Object.assign(copiedJob, await rollSubPayTermsOnto(copiedJob));

      await updateDoc(oldJobRef, {
        status: "Completed",
        completedDates: arrayUnion(oldDate),
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectContinuedToDate: nextDate,
        projectContinuedToWO: newWO || selectedJob.wo || "",
        updatedAt: serverTimestamp(),
      });
  
      const newDocRef = await addDoc(collection(db, "jobs"), copiedJob);
  
      await updateDoc(oldJobRef, {
        projectContinuedToJobId: newDocRef.id,
      });
  
      const completedOldJob = {
        ...selectedJob,
        status: "Completed",
        completedDates: [...(selectedJob.completedDates || []), oldDate],
        isProject: true,
        projectStatus: "Active",
        projectRootId,
        projectContinuedToDate: nextDate,
        projectContinuedToWO: newWO || selectedJob.wo || "",
        projectContinuedToJobId: newDocRef.id,
      };
  
      const newJobForState = {
        ...copiedJob,
        id: newDocRef.id,
        creationDate: new Date(),
        updatedAt: new Date(),
      };
  
      setJobs((prev) => [
        ...prev.map((job) =>
          job.id === selectedJob.id ? completedOldJob : job
        ),
        newJobForState,
      ]);
  
      setShowVerificationPopup(false);
      setSelectedJob(null);
  
      alert(`Project day completed. New project day created for ${nextDate}.`);
    } catch (err) {
      console.error("Error completing project day:", err);
      alert("Could not complete this project day.");
    }
  };

  const markProjectFullyCompleted = async () => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob?.id) return;
  
    const completionDate = window.prompt(
      "Enter the final project completion date:",
      todayKey()
    );
  
    if (!completionDate) return;
  
    const ok = window.confirm(
      `Mark this entire project completed as of ${completionDate}? This will not create another project day.`
    );
  
    if (!ok) return;
  
    try {
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        status: "Completed",
        isProject: true,
        projectStatus: "Project Completed",
        projectCompletedAt: completionDate,
        projectCompletedBy: userFullName || "User",
        completedDates: arrayUnion(completionDate),
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? {
                ...job,
                status: "Completed",
                isProject: true,
                projectStatus: "Project Completed",
                projectCompletedAt: completionDate,
                projectCompletedBy: userFullName || "User",
                completedDates: [...(job.completedDates || []), completionDate],
              }
            : job
        )
      );
  
      setSelectedJob(null);
      alert("Project marked completed.");

      setBillingPromptJob({
        ...selectedJob,
        status: "Completed",
        isProject: true,
        projectStatus: "Project Completed",
        projectCompletedAt: completionDate,
        completedDates: [...(selectedJob.completedDates || []), completionDate],
      });
    } catch (err) {
      console.error("Error completing project:", err);
      alert("Could not mark project completed.");
    }
  };


  const markSourceProposalCompleted = async (job) => {
    if (!job?.proposalId) return;

    try {
      await updateDoc(doc(db, "proposals", job.proposalId), {
        status: "Completed",
        completedFromJobId: job.id || "",
        completedFromWO: job.wo || "",
        proposalCompletedAt: new Date().toISOString(),
        updatedAt: serverTimestamp(),
      });
    } catch (err) {
      console.warn("Could not mark source proposal completed:", err);
    }
  };

  const markJobCompleted = async () => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob) return;
  
    const jobRef = doc(db, "jobs", selectedJob.id);
    const occurrenceKey = getOccurrenceDateKey(selectedJob);
  
    try {
      if (isProjectJob(selectedJob)) {
        // No auto check-out here: this flow can be cancelled at its confirm
        // and doesn't report back. They check out themselves (or on clock-out).
        await completeProjectDayAndCreateNext();
        return;
      }
      if (isRecurringJob(selectedJob)) {
        // Recurring: append ONLY this occurrence's date. The shared `status`
        // field is deliberately left alone - writing it there would close the
        // entire series everywhere that gates on status.
        const result = await markOccurrenceCompleted(selectedJob);
        if (!result) return;
        await jobVisits.checkOutIfAt(selectedJob, "job_completed");
        if (result.dateMissing) {
          alert(
            `Job completed. This recurring job had no date, so it was recorded under today (${result.occurrenceKey}).\n\nYou can add the correct date later in Edit Job.`
          );
        }

        setJobs(prev =>
          prev.map(j =>
            j.id === selectedJob.id ? { ...j, ...result.patch } : j
          )
        );

        setShowVerificationPopup(false);
        setSelectedJob(null);
        return;
      }
      
  
      // ✅ ONE-TIME JOB
      await updateDoc(jobRef, {
        status: "Completed",
        completedDates: arrayUnion(occurrenceKey),
      });
      await markSourceProposalCompleted(selectedJob);
      await jobVisits.checkOutIfAt(selectedJob, "job_completed");

      // Ask whether to notify the client. This branch writes the completion
      // directly instead of going through markOccurrenceCompleted, so it has
      // to prompt for itself. After the write, so a declined or failed email
      // can never undo a completed job.
      try {
        await confirmAndSendCompletionEmail(selectedJob, { occurrenceKey });
      } catch (emailErr) {
        console.error("Completion email step failed (job is still completed):", emailErr);
      }

      // 🔥 update LOCAL STATE
      setJobs(prev =>
        prev.map(j =>
          j.id === selectedJob.id
            ? {
                ...j,
                status: "Completed",
                completedDates: [...(j.completedDates || []), occurrenceKey],
              }
            : j
        )
      );
  
      setShowVerificationPopup(false);
      setSelectedJob(null);

      // Straight to Approved Invoices: ask what kind of invoice this is.
      setBillingPromptJob({
        ...selectedJob,
        status: "Completed",
        completedDates: [...(selectedJob.completedDates || []), occurrenceKey],
      });
    } catch (err) {
      console.error("Error marking job completed:", err);
      alert(t("Could not mark job completed."));
    }
  };
  
  
  const openTotalPopup = async (job) => {
    // Totals are office-only; techs and subs never open this.
    if (isTech) return;
    const jobKey = String(job.wo ?? job.id);
  
    setSelectedJob(job);
    setTotalReason("");
    setTotalLedger(null);

    let ledger = null;
    try {
      ledger = await loadContractLedger(getContractIdForJob(job));
    } catch (err) {
      console.warn("Job Money lookup failed; using the job total:", err);
    }

    if (ledger && ledgerOwnsTotal(ledger)) {
      setTotalLedger(ledger);
      setTotalInput(String(computeContractTotals(ledger).original));
    } else {
      setTotalInput(jobTotals[jobKey] ?? "");
    }
    setShowTotalPopup(true);
  };  
  
  const saveTotal = async () => {
  
    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob || totalInput === "") return;

    const jobKey = String(selectedJob.wo ?? selectedJob.id);

    // Job Money holds this job's total: correct the ORIGINAL there, logged
    // with a reason. Change orders / adjustments stay on top of it.
    if (totalLedger) {
      if (!totalReason.trim()) {
        alert("Please enter a reason for correcting the original total.");
        return;
      }
      try {
        const next = await correctOriginalTotal(totalLedger, {
          newOriginal: totalInput,
          reason: totalReason,
        });
        const current = computeContractTotals(next).current;
        await pushNewTotalToOpenInvoices({
          contractId: getContractIdForJob(selectedJob),
          newTotal: current,
          invoices: await loadContractInvoices(getContractIdForJob(selectedJob)),
          refreshHeader: getRefreshedHeaderPatch,
        });
        // Pages that read the job's own total see the current contract total.
        await addJobTotal({
          jobId: jobKey,
          wo: selectedJob.wo ? String(selectedJob.wo) : null,
          proposalId: selectedJob.proposalId || null,
          total: current,
          source: "job-money",
        });
        await updateDoc(doc(db, "jobs", selectedJob.id), { estimatedTotal: current });
        setJobTotals((prev) => ({ ...prev, [jobKey]: current }));
        setTotalLedger(null);
        setShowTotalPopup(false);
      } catch (err) {
        console.error("Correcting the original total failed:", err);
        alert("Could not save the corrected total. Please try again.");
      }
      return;
    }
  
    // 🔒 ALWAYS force string ID (jobKey above)
  
    await addJobTotal({
      jobId: jobKey,
      wo: selectedJob.wo ? String(selectedJob.wo) : null,
      proposalId: selectedJob.proposalId || null,
      total: Number(totalInput),
      source: "job",
    });

    // keep job.estimatedTotal in sync too, same as the SendInvoices page,
    // so anything still reading estimatedTotal directly stays correct
    await updateDoc(doc(db, "jobs", selectedJob.id), {
      estimatedTotal: Number(totalInput),
    });
  
    // Contract jobs (deposit / progress / final) bill from the contract total
    // saved on the job, not estimatedTotal. Update it too, plus any draft
    // invoices on the contract, so Change Total really changes what is billed.
    if (selectedJob.contractBilling?.contractId) {
      try {
        const contractId = getContractIdForJob(selectedJob);
        await pushNewTotalToOpenInvoices({
          contractId,
          newTotal: Number(totalInput),
          invoices: await loadContractInvoices(contractId),
          refreshHeader: getRefreshedHeaderPatch,
        });
      } catch (err) {
        console.warn("Could not update the contract total:", err);
      }
    }

    // update UI instantly
    setJobTotals(prev => ({
      ...prev,
      [jobKey]: Number(totalInput),
    }));
    setSelectedJob((prev) =>
      prev && prev.id === selectedJob.id
        ? {
            ...prev,
            estimatedTotal: Number(totalInput),
            ...(prev.contractBilling?.contractId
              ? { contractBilling: { ...prev.contractBilling, contractTotal: Number(totalInput) } }
              : {}),
          }
        : prev
    );
  
    setShowTotalPopup(false);
  };
  
  const addMaterial = () => {
    setMaterialsDraft((prev) => [
      ...prev,
      {
        name: "",
        qty: 1,
        unit: "pcs",
        costPer: "",
        purchaseLocation: "",
        notes: "",
        purchased: false,
        files: [],
      },
    ]);
  };

  const updateMaterialDraft = (index, patch) => {
    setMaterialsDraft((prev) =>
      prev.map((material, materialIndex) =>
        materialIndex === index ? { ...material, ...patch } : material
      )
    );
  };

  const removeMaterialDraft = (index) => {
    setMaterialsDraft((prev) =>
      prev.filter((_, materialIndex) => materialIndex !== index)
    );
  };

  const uploadMaterialFiles = async (index, fileList) => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob) return;
    const files = Array.from(fileList || []);
    if (!files.length) return;

    const groupId = getMaterialsGroupId(selectedJob);
    const uploaded = [];

    for (const file of files) {
      const fileRef = ref(
        getStorage(),
        `materialFiles/${groupId}/${Date.now()}_${file.name}`
      );
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);
      uploaded.push({ url, name: file.name });
    }

    setMaterialsDraft((previous) =>
      previous.map((material, materialIndex) =>
        materialIndex === index
          ? { ...material, files: [...(material.files || []), ...uploaded] }
          : material
      )
    );
  };

  const removeMaterialFile = (materialIndex, fileIndex) => {
    setMaterialsDraft((previous) =>
      previous.map((material, index) =>
        index === materialIndex
          ? {
              ...material,
              files: (material.files || []).filter((_, fi) => fi !== fileIndex),
            }
          : material
      )
    );
  };

  const getEstimatedMaterialCost = (material) => {
    const qty = Number(material?.qty || 0);
    const costPer = Number(material?.costPer || 0);
    return qty * costPer;
  };
  
  const handleAdminPortalClick = () => {
    if (!canAccessAdminPortal) {
      window.alert("🚫 Access denied: You don’t have permission to view the Admin Portal.");
      navigate("/"); // back to Home
      return;
    }
    navigate("/SupervisorDashboard");
  };
  
  const updateMaterialStatus = async (jobId, materialIndex, patch) => {
  
    if (blockIfViewingAs()) return; // "View as" is read-only
    const job = jobs.find(j => j.id === jobId);
    if (!job) return;

    const groupId = getMaterialsGroupId(job);
    const currentMaterials = jobMaterials[groupId] || (await loadMaterialsForJob(job));
    const nextMaterials = currentMaterials.map((m, idx) =>
      idx === materialIndex ? { ...m, ...patch } : m
    );

    await saveMaterialsForJob(job, nextMaterials);

    // keep UI in sync instantly
    setJobMaterials((prev) => ({ ...prev, [groupId]: nextMaterials }));
  };
  
  const saveMaterials = async () => {
  
    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob?.id) return;
  
    try {
      const groupId = getMaterialsGroupId(selectedJob);

      await saveMaterialsForJob(selectedJob, materialsDraft);

      // ✅ Keep UI in sync (both list + popup job)
      setJobMaterials((prev) => ({ ...prev, [groupId]: materialsDraft }));

      setShowMaterialsPopup(false);
    } catch (err) {
      console.error("Error saving materials:", err);
      alert(t("Could not save materials. Please try again."));
    }
  };
  
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setSelectedJob((prev) => ({
      ...prev,
      newFiles: files.map((file) => URL.createObjectURL(file)),
    }));
  };

  const uploadFiles = async (files) => {
    const storage = getStorage();
    const uploadPromises = files.map(async (file) => {
      const storageRef = ref(storage, `notes/${selectedJob.id}/${file.name}`);
      await uploadBytes(storageRef, file);
      return await getDownloadURL(storageRef);
    });
    return await Promise.all(uploadPromises);
  };

  const saveReceipt = async () => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob?.id) return;
  
    if (!receiptAmount && !receiptFile) {
      alert(t("Please add a receipt amount or upload a receipt image."));
      return;
    }
  
    setSavingReceipt(true);
  
    try {
      let fileURL = "";
      let fileName = "";
  
      if (receiptFile) {
        const storage = getStorage();
        const fileRef = ref(
          storage,
          `jobReceipts/${selectedJob.id}/${Date.now()}_${receiptFile.name}`
        );
  
        await uploadBytes(fileRef, receiptFile);
        fileURL = await getDownloadURL(fileRef);
        fileName = receiptFile.name;
      }
  
      const newReceipt = {
        amount: Number(receiptAmount || 0),
        note: receiptNote || "",
        lang,
        fileURL,
        fileName,
        uploadedBy: userFullName || "User",
        uploadedAt: new Date().toISOString(),
      };
  
      const updatedReceipts = Array.isArray(selectedJob.receipts)
        ? [...selectedJob.receipts, newReceipt]
        : [newReceipt];
  
      const receiptsTotal = updatedReceipts.reduce(
        (sum, receipt) => sum + Number(receipt.amount || 0),
        0
      );
  
      await updateDoc(doc(db, "jobs", selectedJob.id), {
        receipts: updatedReceipts,
        receiptsTotal,
        updatedAt: serverTimestamp(),
      });
      await markSourceProposalCompleted(selectedJob);
  
      setJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, receipts: updatedReceipts, receiptsTotal }
            : job
        )
      );
  
      setFilteredJobs((prev) =>
        prev.map((job) =>
          job.id === selectedJob.id
            ? { ...job, receipts: updatedReceipts, receiptsTotal }
            : job
        )
      );
  
      setSelectedJob((prev) => ({
        ...prev,
        receipts: updatedReceipts,
        receiptsTotal,
      }));
  
      setReceiptAmount("");
      setReceiptFile(null);
      setReceiptNote("");
      setShowReceiptPopup(false);
    } catch (err) {
      console.error("Error saving receipt:", err);
      alert(t("Could not save receipt."));
    } finally {
      setSavingReceipt(false);
    }
  };

  const addSupervisorNote = async (jobOverride = null) => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    const targetJob = jobOverride || selectedJob;
    if (!targetJob?.id) return;
  
    if (!canSeeSupervisorNotes) {
      alert("You do not have permission to add supervisor notes.");
      return;
    }
  
    const text = window.prompt("Add supervisor note:");
    if (!text || !text.trim()) return;
  
    const isRecurring = isRecurringJob(targetJob);
    const occurrenceKey = isRecurring ? getOccurrenceDateKey(targetJob) : null;

    const newSupervisorNote = {
      id: `${Date.now()}_${Math.random().toString(36).slice(2)}`,
      text: text.trim(),
      author: userFullName || "User",
      authorRole: userRole || "",
      createdAt: new Date().toISOString(),
      checked: false,
      checkedBy: "",
      checkedAt: "",
      occurrenceDate: occurrenceKey,
    };
  
    const updatedSupervisorNotes = Array.isArray(targetJob.supervisorNotes)
      ? [...targetJob.supervisorNotes, newSupervisorNote]
      : [newSupervisorNote];
  
    await updateDoc(doc(db, "jobs", targetJob.id), {
      supervisorNotes: updatedSupervisorNotes,
    });
  
    setJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === targetJob.id
          ? { ...job, supervisorNotes: updatedSupervisorNotes }
          : job
      )
    );
  
    setFilteredJobs((prevJobs) =>
      prevJobs.map((job) =>
        job.id === targetJob.id
          ? { ...job, supervisorNotes: updatedSupervisorNotes }
          : job
      )
    );
  
    setSelectedJob((prev) => ({
      ...prev,
      supervisorNotes: updatedSupervisorNotes,
    }));
  };

  const saveNote = async () => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob || (noteText.trim() === "" && selectedFiles.length === 0)) {
      return;
    }
  
    setUploading(true);
  
    try {
      const occurrenceKey = getOccurrenceDateKey(selectedJob);
      const isRecurring = isRecurringJob(selectedJob);
  
      let fileUploads = [];
  
      if (selectedFiles.length > 0) {
        const storage = getStorage();
  
        fileUploads = await Promise.all(
          selectedFiles.map(async (file) => {
            const folderKey = isRecurring ? occurrenceKey : "main";
  
            const fileRef = ref(
              storage,
              `notes/${selectedJob.id}/${folderKey}/${Date.now()}_${file.name}`
            );
  
            await uploadBytes(fileRef, file);
            const url = await getDownloadURL(fileRef);
  
            return { fileURL: url, fileName: file.name };
          })
        );
      }
  
      const newNote = {
        text: noteText,
        timestamp: new Date().toLocaleString(),
        author: userFullName || "User",
        files: fileUploads,
        occurrenceDate: isRecurring ? occurrenceKey : null,
        // Language the note was written in, so it can be translated for readers.
        lang,
      };
  
      const jobDocRef = doc(db, "jobs", selectedJob.id);
  
      if (isRecurring) {
        const freshSnap = await getDoc(jobDocRef);
        const freshData = freshSnap.exists() ? freshSnap.data() : {};
  
        const existingOccurrenceNotes = freshData.occurrenceNotes || {};
        const existingNotesForDate = existingOccurrenceNotes[occurrenceKey] || [];
  
        const updatedOccurrenceNotes = {
          ...existingOccurrenceNotes,
          [occurrenceKey]: [...existingNotesForDate, newNote],
        };
  
        await updateDoc(jobDocRef, {
          occurrenceNotes: updatedOccurrenceNotes,
        });
  
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id
              ? { ...job, occurrenceNotes: updatedOccurrenceNotes }
              : job
          )
        );
  
        setFilteredJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id
              ? { ...job, occurrenceNotes: updatedOccurrenceNotes }
              : job
          )
        );
  
        setSelectedJob((prev) => ({
          ...prev,
          occurrenceNotes: updatedOccurrenceNotes,
        }));
      } else {
        const updatedNotes = Array.isArray(selectedJob.notes)
          ? [...selectedJob.notes, newNote]
          : [newNote];
  
        await updateDoc(jobDocRef, { notes: updatedNotes });

        try {
          await sendJobNoteEmailCallable({
            jobId: selectedJob.id,
            noteIndex: updatedNotes.length - 1,
          });
        } catch (emailErr) {
          console.error("Note saved, but note email failed:", emailErr);
        }
  
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, notes: updatedNotes } : job
          )
        );
  
        setFilteredJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === selectedJob.id ? { ...job, notes: updatedNotes } : job
          )
        );
  
        setSelectedJob((prev) => ({
          ...prev,
          notes: updatedNotes,
        }));
      }
  
      setNoteText("");
      setSelectedFiles([]);
      setShowNotePopup(false);
    } catch (err) {
      console.error("Error saving note:", err);
      alert(t("There was an error saving your note. Please try again."));
    } finally {
      setUploading(false);
    }
  };

  const getBackgroundColor = (startDate) => {
    const date = moment(startDate);
    const now = moment();
    const diffDays = now.diff(date, "days");

    if (diffDays > 30) {
      return "red";
    } else if (diffDays > 1) {
      return "orange";
    } else {
      return "white";
    }
  };
  const rightNavTabs = [
    { label: "Clock Time", tab: "clock-time", path: "/ClockTime" },
    { label: "Customer Codes", tab: "customer-codes", path: "/CustomerCodes" },
    { label: "Maps", tab: "maps", path: "/Maps" },
    { label: "Time Off", tab: "time-off", path: "/TimeOff" },
    { label: "Warehouse", tab: "warehouse", path: "/Supplies" },
  ];
  
  
  const navTabStyle = {
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    color: "#0f172a",
    fontWeight: 700,
    cursor: "pointer",
    whiteSpace: "nowrap",
  
    minWidth: 140,              // ✅ prevents squishing
    flex: "0 0 auto",           // ✅ keeps button size stable
  };  
  
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleCompanyWorkOrderSearchChange = (e) => {
    setCompanyWorkOrderSearch(e.target.value);
  };

  const handleClientWorkOrderSearchChange = (e) => {
    setClientWorkOrderSearch(e.target.value);
  };

  const getReceiptsTotal = (job) => {
    if (!Array.isArray(job?.receipts)) return 0;
  
    return job.receipts.reduce((sum, receipt) => {
      return sum + Number(receipt.amount || 0);
    }, 0);
  };

  // 🔍 helper like WorkOrders
  const normalize = (v) => (v || "").toString().toLowerCase();

  const matchesSearch = (job) => {
    const term = normalize(searchTerm);
    if (!term) return true;
  
    return (
      normalize(job.siteName).includes(term) ||
      normalize(job.city).includes(term) ||
      normalize(job.state).includes(term) ||
      normalize(job.scope).includes(term) ||
  
      // ✅ WO searches (support alt field names just in case)
      normalize(job.wo).includes(term) ||
      normalize(job.workOrder).includes(term) ||
      normalize(job.workOrderNumber).includes(term) ||
  
      normalize(job.clientWorkOrder).includes(term)
    );
  };
  

  const handleSortDropdownChange = (e) => {
    setSortOption(e.target.value);
  };

  const filterAndSortJobs = () => {
    let data = [...expandedHomeJobs];
// ✅ ALWAYS show Active-only in list (hide completed one-time + completed occurrences)
data = data.filter((job) => !isJobCompleted(job));

// ✅ hide invoice-sent one-time jobs from the Jobs tab list.
// (For recurring series, job.status lives on the shared master doc and
// gets set to "Invoice Sent" when ANY single occurrence is invoiced —
// see SendInvoices.js handleInvoiceSent — so it must not hide the whole
// series here.)
data = data.filter((job) => isRecurringJob(job) || job.status !== "Invoice Sent");


    // filter by site
    if (selectedSite !== "ALL_SITES") {
      data = data.filter((j) => (j.siteName || "") === selectedSite);
    }

    // filter by technician (Admin)
if (selectedTech !== "ALL_TECHS") {
  data = data.filter((job) => {
    if (Array.isArray(job.techAssign)) {
      return job.techAssign.includes(selectedTech);
    }
    return job.techAssign === selectedTech;
  });
}

// 🔴 date-based summary filter
if (dateFilter) {
  const tKey = todayKey();
  const tmKey = tomorrowKey();
  const eowKey = endOfWeekKey();
  const nextWeekStartKey = startOfNextWeekKey();
  const nextWeekEndKey = endOfNextWeekKey();

  data = data.filter((job) => {
    const startKey = getJobStartKey(job);

    switch (dateFilter) {
      case "today":
        return jobSpansDate(job, tKey);

      case "tomorrow":
        return jobSpansDate(job, tmKey);

      case "week":
        return jobSpansRange(job, tmKey, eowKey);

      case "nextWeek":
        return jobSpansRange(job, nextWeekStartKey, nextWeekEndKey);

      case "overdue":
        return isMissedRenderedJob(job);

      case "needsScheduling":
        return !startKey;

      case "allActive":
        return true;

      default:
        return true;
    }
  });
}
    // filter by specific start date (if selected)
    if (selectedStartDate) {
      data = data.filter((j) =>
        toDateKey(j.startDate) === selectedStartDate
      );
    }
    

    // search
    data = data.filter(matchesSearch);

    // sort
    // 🔤 DEFAULT SORT: Site Name A → Z
data.sort((a, b) =>
  (a.siteName || "").localeCompare(b.siteName || "", undefined, {
    sensitivity: "base",
  })
);

// 🔁 USER-SELECTED SORT OVERRIDES DEFAULT
if (sortOption === "startDateAsc") {
  data.sort(
    (a, b) =>
      moment(toDateKey(a.startDate)).valueOf() -
      moment(toDateKey(b.startDate)).valueOf()
  );
} else if (sortOption === "startDateDesc") {
  data.sort(
    (a, b) =>
      moment(toDateKey(b.startDate)).valueOf() -
      moment(toDateKey(a.startDate)).valueOf()
  );
}
 else if (sortOption === "cityAsc") {
  data.sort((a, b) => (a.city || "").localeCompare(b.city || ""));
} else if (sortOption === "cityDesc") {
  data.sort((a, b) => (b.city || "").localeCompare(a.city || ""));
}


    setFilteredJobs(data);
  };

  const saveQuickJobFields = async (changes) => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob?.id) return;

    const editedQuickJob = { ...selectedJob, ...changes };
    const result = await saveRecurringJobEdit({
      originalJob: selectedJob,
      editedJob: editedQuickJob,
    });

    if (result.mode === "cancel") return false;

    let nextSelectedJob = editedQuickJob;
    if (result.mode === "future") {
      setJobs((prev) => [
        ...prev.map((job) => (job.id === result.oldJob.id ? result.oldJob : job)),
        result.newJob,
      ]);
      nextSelectedJob = result.newJob;
    } else if (result.mode === "occurrence") {
      setJobs((prev) =>
        prev.map((job) => (job.id === result.masterJob.id ? result.masterJob : job))
      );
      nextSelectedJob = result.updatedJob;
    } else {
      setJobs((prev) =>
        prev.map((job) => (job.id === result.updatedJob.id ? result.updatedJob : job))
      );
      nextSelectedJob = result.updatedJob;
    }

    setSelectedJob(nextSelectedJob);
    setEditedJob(nextSelectedJob);
    return true;
  };

  // ✅ FIX: Save button in edit form – no form submit
  const handleSaveChanges = async (e) => {
    if (blockIfViewingAs()) return; // "View as" is read-only
    if (e) e.preventDefault();
    if (!editedJob?.id || !selectedJob) return;

    const mismatch = getDurationMismatch(editedJob);

    if (mismatch) {
      const ok = window.confirm(
        `Scheduled for ${mismatch.scheduledDays} days but estimated ${mismatch.estimatedDays} days.\n\nSave anyway?`
      );

      if (!ok) return;
    }

    try {
      const result = await saveRecurringJobEdit({
        originalJob: selectedJob,
        editedJob,
      });

      if (result.mode === "cancel") return;

      if (result.mode === "future") {
        setJobs((prevJobs) => [
          ...prevJobs.map((job) =>
            job.id === result.oldJob.id ? result.oldJob : job
          ),
          result.newJob,
        ]);

        setSelectedJob(result.newJob);
        setEditedJob(result.newJob);
      } else if (result.mode === "occurrence") {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.masterJob.id ? result.masterJob : job
          )
        );

        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      } else {
        setJobs((prevJobs) =>
          prevJobs.map((job) =>
            job.id === result.updatedJob.id ? result.updatedJob : job
          )
        );

        setSelectedJob(result.updatedJob);
        setEditedJob(result.updatedJob);
      }

      setIsEditing(false);
      alert("Job updated successfully!");
    } catch (error) {
      console.error("Error updating job:", error);
      alert("Failed to update job.");
    }
  };

  const handleDeleteJob = async () => {

    if (blockIfViewingAs()) return; // "View as" is read-only
    if (!selectedJob) return;
    try {
      const result = await closeOrDeleteJob({ job: selectedJob });

      if (result.mode === "cancel") return;

      if (result.mode === "deleted") {
        setJobs((prev) => prev.filter((job) => job.id !== result.jobId));
        setSelectedJob(null);
        alert("Job deleted successfully.");
        return;
      }

      // "occurrence" or "future" - job document still exists, just updated.
      setJobs((prev) =>
        prev.map((job) =>
          job.id === result.updatedJob.id ? { ...job, ...result.updatedJob } : job
        )
      );
      setSelectedJob(null);
      alert(
        result.mode === "future"
          ? "This job's future occurrences have been closed. Its history is unchanged."
          : "This occurrence has been closed."
      );
    } catch (error) {
      console.error("Error closing job:", error);
      alert("Failed to close this job.");
    }
  };

  const toggleExportJob = (jobKey) => {
    setSelectedExportJobIds((prev) =>
      prev.includes(jobKey)
        ? prev.filter((id) => id !== jobKey)
        : [...prev, jobKey]
    );
  };
  
  const getJobExportKey = (job) =>
    `${job.id}_${job.occurrenceDate || toDateKey(job.startDate) || "UNSCHEDULED"}`;
  
  const getExportJobTotal = (job) => {
    const candidates = [
      jobTotals?.[job.id],
      job?.estimatedTotal,
      job?.jobTotal,
      job?.total,
      job?.amount,
    ];

    const found = candidates.find(
      (value) =>
        value !== "" &&
        value !== null &&
        value !== undefined &&
        Number.isFinite(Number(value))
    );

    return found === undefined ? 0 : Number(found);
  };

  /* Resolved bounds. Whole weeks and whole months, because this looks ahead. */
  const [pipeFrom, pipeTo] = useMemo(() => {
    const d = moment();
    if (pipelineRange === "today") return [d.format("YYYY-MM-DD"), d.format("YYYY-MM-DD")];
    if (pipelineRange === "tomorrow") {
      const t = moment().add(1, "day").format("YYYY-MM-DD");
      return [t, t];
    }
    if (pipelineRange === "week")
      return [moment().startOf("week").format("YYYY-MM-DD"),
              moment().endOf("week").format("YYYY-MM-DD")];
    if (pipelineRange === "nextweek")
      return [moment().add(1, "week").startOf("week").format("YYYY-MM-DD"),
              moment().add(1, "week").endOf("week").format("YYYY-MM-DD")];
    if (pipelineRange === "month")
      return [moment().startOf("month").format("YYYY-MM-DD"),
              moment().endOf("month").format("YYYY-MM-DD")];
    /* Swapped inputs still give the range that was obviously meant. */
    const a = pipelineFrom || d.format("YYYY-MM-DD");
    const b = pipelineTo || d.format("YYYY-MM-DD");
    return a <= b ? [a, b] : [b, a];
  }, [pipelineRange, pipelineFrom, pipelineTo]);

  const pipeline = useMemo(() => {
    if (!isAdmin) return null;

    const byDay = new Map();
    let count = 0;
    let amount = 0;
    let missing = 0;
    let unscheduled = 0;
    // A contract (deposit / progress / final) is counted once, at what is
    // still left to bill on it - not its full total on every continued WO.
    // Before, a $10,000 contract with a $5,000 deposit paid showed $10,000 on
    // -2, and again on -3 after a Continue.
    const contractsCounted = new Set();

    (jobs || []).forEach((job) => {
      if (String(job?.status || "").trim() !== "Active") return;

      const day = toDateKey(job.startDate);
      if (!day) { unscheduled += 1; return; }
      if (day < pipeFrom || day > pipeTo) return;

      const contractId = job?.contractBilling?.contractId;
      let value = getExportJobTotal(job);
      if (contractId) {
        if (contractsCounted.has(contractId)) {
          count += 1;
          const row = byDay.get(day) || { day, count: 0, amount: 0 };
          row.count += 1;
          byDay.set(day, row);
          return;
        }
        contractsCounted.add(contractId);
        const contractTotal = Number(job.contractBilling.contractTotal) || value;
        value = Math.max(0, Math.round((contractTotal - (contractBilled[contractId] || 0)) * 100) / 100);
      }
      count += 1;
      amount += value;
      /* Jobs with no total entered are the reason a pipeline number looks
         low. Surfaced rather than hidden, so the gap is visible instead of
         being mistaken for a slow week. */
      if (!value) missing += 1;

      const row = byDay.get(day) || { day, count: 0, amount: 0 };
      row.count += 1;
      row.amount += value;
      byDay.set(day, row);
    });

    return {
      count,
      amount,
      missing,
      unscheduled,
      days: Array.from(byDay.values()).sort((a, b) => a.day.localeCompare(b.day)),
    };
  }, [isAdmin, jobs, pipeFrom, pipeTo, jobTotals, contractBilled]);

  useEffect(() => {
    if (!isAdmin || !pipelineOpen) return;
    let cancelled = false;
    getDocs(query(collection(db, "approvedInvoices"), where("billingKind", "in", ["deposit", "progress", "final"])))
      .then((snap) => {
        if (cancelled) return;
        const map = {};
        snap.docs.forEach((d) => {
          const inv = d.data();
          if (!inv.contractId) return;
          const amt = Number(String(inv.amount ?? inv.rate ?? 0).replace(/[^0-9.-]/g, "")) || 0;
          map[inv.contractId] = (map[inv.contractId] || 0) + amt;
        });
        setContractBilled(map);
      })
      .catch((err) => console.warn("Pipeline: could not read contract invoices", err));
    return () => {
      cancelled = true;
    };
  }, [isAdmin, pipelineOpen]);


  const inferExportSkills = (job = {}) => {
    const text = `${job.serviceType || ""} ${job.scope || ""} ${job.jobDescription || ""}`.toLowerCase();

    const rules = [
      ["Electrical", ["electric", "outlet", "switch", "breaker", "light fixture", "gfci"]],
      ["Plumbing", ["plumb", "pipe", "toilet", "sink", "faucet", "hose bib", "water heater"]],
      ["HVAC", ["hvac", "air conditioning", "heat pump", "furnace"]],
      ["Painting", ["paint", "primer", "stain"]],
      ["Drywall", ["drywall", "sheetrock", "gypsum", "patch wall"]],
      ["Framing", ["framing", "frame", "stud", "rough carpentry"]],
      ["Carpentry", ["carpentry", "trim", "cabinet", "shelving", "wood"]],
      ["Flooring", ["floor", "lvp", "vinyl plank", "hardwood", "carpet"]],
      ["Tile", ["tile", "grout", "backsplash", "shower pan"]],
      ["Concrete", ["concrete", "sidewalk", "curb", "slab"]],
      ["Masonry", ["brick", "block", "masonry", "stone", "cmu"]],
      ["Roofing", ["roof", "shingle", "flashing"]],
      ["Fencing", ["fence", "gate", "picket", "post"]],
      ["Decking", ["deck", "railing", "joist"]],
      ["Landscaping", ["landscape", "mulch", "tree", "plant"]],
      ["Drainage", ["drain", "downspout", "french drain"]],
      ["Cleaning", ["clean", "janitorial"]],
      ["Bulk Trash", ["bulk trash", "trash out", "debris removal", "haul"]],
      ["Demo", ["demo", "demolition", "remove"]],
    ];

    const matches = rules
      .filter(([, keywords]) => keywords.some((keyword) => text.includes(keyword)))
      .map(([skill]) => skill);

    return matches.length
      ? Array.from(new Set(matches))
      : [job.serviceType || "Maintenance"];
  };

  const buildHomeExportRecommendation = (job, workingJobs = []) => {
    const today = moment().startOf("day");
    const startKey = toDateKey(job.startDate);
    const isPastDue = Boolean(
      startKey && moment(startKey, "YYYY-MM-DD").isBefore(today, "day")
    );
    const isUnscheduled = !startKey;

    if (!isPastDue && !isUnscheduled) return null;

    const requiredSkills = inferExportSkills(job);
    const activeStaff = filterStaffForDropdowns(usersList)
      .map((user) => ({
        ...user,
        displayName:
          `${user.firstName || ""} ${user.lastName || ""}`.trim() ||
          user.name ||
          user.email ||
          "",
      }))
      .filter((user) => user.displayName);

    const normalize = (value = "") =>
      String(value)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, " ")
        .trim();

    const skillMatches = activeStaff.filter((user) => {
      const role = normalize(user.role);
      const position = normalize(user.position);
      const skills = Array.isArray(user.skills)
        ? user.skills.map(normalize)
        : [];

      if (
        role === "supervisor" ||
        position.includes("supervisor") ||
        skills.includes("all")
      ) {
        return true;
      }

      return requiredSkills.some((required) => {
        const req = normalize(required);
        return skills.some(
          (skill) =>
            skill === req ||
            skill.includes(req) ||
            req.includes(skill)
        );
      });
    });

    const candidates = skillMatches.length ? skillMatches : activeStaff;
    if (!candidates.length) return null;

    const estimatedDays = Math.max(
      1,
      Number(job.jobPlan?.estimatedDays || 1)
    );
    const laborNeeded = Math.max(
      1,
      Number(job.jobPlan?.estimatedLabor || 1)
    );

    const assignedByDate = new Map();

    workingJobs.forEach((existingJob) => {
      if (
        ["Completed", "Invoice Sent", "Deleted"].includes(existingJob.status)
      ) {
        return;
      }

      const dateKey = toDateKey(
        existingJob.startDate || existingJob.occurrenceDate
      );
      if (!dateKey) return;

      const names = Array.isArray(existingJob.techAssign)
        ? existingJob.techAssign.filter(Boolean)
        : existingJob.techAssign
          ? [existingJob.techAssign]
          : [];

      if (!assignedByDate.has(dateKey)) {
        assignedByDate.set(dateKey, new Set());
      }

      names.forEach((name) => assignedByDate.get(dateKey).add(name));
    });

    const cursor = today.clone();

    for (let searched = 0; searched < 180; searched += 1) {
      if (cursor.day() !== 0 && cursor.day() !== 6) {
        const proposedDates = [];
        const spanCursor = cursor.clone();

        while (proposedDates.length < estimatedDays) {
          if (spanCursor.day() !== 0 && spanCursor.day() !== 6) {
            proposedDates.push(spanCursor.format("YYYY-MM-DD"));
          }
          spanCursor.add(1, "day");
        }

        const ranked = candidates
          .map((user) => {
            const conflictCount = proposedDates.reduce(
              (count, dateKey) =>
                count +
                (assignedByDate.get(dateKey)?.has(user.displayName) ? 1 : 0),
              0
            );

            return { ...user, conflictCount };
          })
          .filter((user) => user.conflictCount === 0)
          .sort((a, b) =>
            a.displayName.localeCompare(b.displayName)
          );

        if (ranked.length >= laborNeeded) {
          return {
            startDate: proposedDates[0],
            endDate: proposedDates[proposedDates.length - 1],
            techAssign: ranked
              .slice(0, laborNeeded)
              .map((user) => user.displayName),
            requiredSkills,
            reason: skillMatches.length
              ? "Matched description/service skills and open schedule"
              : "No exact skill match; recommended available staff",
          };
        }
      }

      cursor.add(1, "day");
    }

    return null;
  };

  const exportSelectedJobsToExcel = async () => {
    // xlsx is large; it is only downloaded when someone actually exports.
    const XLSX = await import("xlsx");
    const selectedJobs = filteredJobs.filter((job) =>
      selectedExportJobIds.includes(getJobExportKey(job))
    );

    if (!selectedJobs.length) {
      alert("No jobs selected for export.");
      return;
    }

    const includeRecommendations = window.confirm(
      "Would you like Excel to recommend a schedule for past-due jobs and jobs with no start date?\n\nClick OK for recommendations, or Cancel for a regular export."
    );

    const workingJobs = [...expandedHomeJobs];
    const recommendationMap = new Map();

    if (includeRecommendations) {
      selectedJobs.forEach((job) => {
        const recommendation = buildHomeExportRecommendation(
          job,
          workingJobs
        );

        if (!recommendation) return;

        recommendationMap.set(
          getJobExportKey(job),
          recommendation
        );

        workingJobs.push({
          ...job,
          startDate: recommendation.startDate,
          endDate: recommendation.endDate,
          techAssign: recommendation.techAssign,
        });
      });
    }

    const rows = selectedJobs.map((job) => {
      const recommendation = recommendationMap.get(
        getJobExportKey(job)
      );

      return {
        JobID: job.id || "",
        WorkOrder: job.wo || "",
        ClientWorkOrder: job.clientWorkOrder || "",
        SiteName: job.siteName || "",
        Address: job.address || "",
        City: job.city || "",
        State: job.state || "",
        StartDate: job.startDate || "",
        EndDate: job.endDate || "",
        OccurrenceDate: job.occurrenceDate || "",
        StartTime:
          job.startTimeMode === "custom"
            ? `Custom (${job.startTime || ""})`
            : job.startTime || "",
        EndTime: job.endTime || "",
        ServiceType: job.serviceType || "",
        Scope: job.scope || "",
        JobDescription: job.jobDescription || "",
        ClientPhone: job.clientPhone || "",
        ClientEmail: job.clientEmail || "",
        TechAssign: Array.isArray(job.techAssign)
          ? job.techAssign.join(", ")
          : job.techAssign || "",
        Status: job.status || "",
        EstimatedDays: job.jobPlan?.estimatedDays || "",
        EstimatedLabor: job.jobPlan?.estimatedLabor || "",
        Materials: (getMaterialsForJob(job) || [])
              .map(
                (m) =>
                  `${m.qty || ""} ${m.unit || ""} ${m.name || ""}${
                    m.notes ? ` (${m.notes})` : ""
                  }`
              )
              .join(" | "),
        JobTotal: getExportJobTotal(job),
        RecommendedStartDate: recommendation?.startDate || "",
        RecommendedEndDate: recommendation?.endDate || "",
        RecommendedTechAssign:
          recommendation?.techAssign?.join(", ") || "",
        RecommendedSkills:
          recommendation?.requiredSkills?.join(", ") || "",
        RecommendationReason: recommendation?.reason || "",
        Notes: Array.isArray(job.notes)
          ? job.notes
              .map(
                (n) => `${n.author || ""}: ${n.text || ""}`
              )
              .join(" | ")
          : "",
        CreatedOn: job.creationDate
          ? new Date(
              job.creationDate.seconds
                ? job.creationDate.seconds * 1000
                : job.creationDate
            ).toLocaleString()
          : "",
        CreatedBy: job.createdBy
          ? `${job.createdBy.firstName || ""} ${
              job.createdBy.lastName || ""
            }`.trim()
          : "",
      };
    });

    const totalAmount = selectedJobs.reduce(
      (sum, job) => sum + getExportJobTotal(job),
      0
    );

    rows.push({
      JobID: "",
      WorkOrder: "",
      ClientWorkOrder: "",
      SiteName: "TOTAL",
      Address: "",
      City: "",
      State: "",
      StartDate: "",
      EndDate: "",
      OccurrenceDate: "",
      StartTime: "",
      EndTime: "",
      ServiceType: "",
      Scope: "",
      JobDescription: "",
      ClientPhone: "",
      ClientEmail: "",
      TechAssign: "",
      Status: "",
      EstimatedDays: "",
      EstimatedLabor: "",
      Materials: "",
      JobTotal: totalAmount,
      RecommendedStartDate: "",
      RecommendedEndDate: "",
      RecommendedTechAssign: "",
      RecommendedSkills: "",
      RecommendationReason: "",
      Notes: "",
      CreatedOn: "",
      CreatedBy: "",
    });

    const ws = XLSX.utils.json_to_sheet(rows);

    ws["!cols"] = Object.keys(rows[0]).map((key) => ({
      wch: Math.min(55, Math.max(12, key.length + 2)),
    }));

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Jobs");

    XLSX.writeFile(
      wb,
      `${
        includeRecommendations
          ? "Jobs_Recommended_Schedule"
          : "Jobs_Export"
      }_${moment().format("YYYY-MM-DD_HH-mm")}.xlsx`
    );
  };

  const selectAllFilteredJobsForExport = () => {
    const keys = filteredJobs.map((job) => getJobExportKey(job));
    setSelectedExportJobIds(keys);
  };
  
  const clearSelectedJobsForExport = () => {
    setSelectedExportJobIds([]);
  };
  
  const clearDateFilters = () => {
    setDateFilter("");
    setSelectedStartDate(""); // optional but recommended
  };

  const handleViewedTeamMessaging = useCallback(() => {
    setTeamHasUnread(false);
    setTeamLastSeenAt(new Date());
  }, []);
  
  const tabsForUser = useMemo(() => {
    if (isTech) {
      return ["clock-calendar"];
    }
  
    if (isStaff) {
      return ["jobs", "clock", "clock-calendar"];
    }
  
    return [];
  }, [isTech, isStaff]);
  
  

  return (
    <div>
      {homeLoadState === "loading" && (
        <div
          style={{
            margin: "0 auto 12px",
            maxWidth: 520,
            padding: "10px 14px",
            borderRadius: 10,
            background: "#eff6ff",
            border: "1px solid #bfdbfe",
            color: "#1e3a8a",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          {t("Loading jobs...")}
        </div>
      )}
      {homeLoadState === "error" && (
        <div
          style={{
            margin: "0 auto 12px",
            maxWidth: 520,
            padding: "12px 14px",
            borderRadius: 10,
            background: "#fef2f2",
            border: "1px solid #fecaca",
            color: "#991b1b",
            textAlign: "center",
            fontWeight: 700,
          }}
        >
          {t("Jobs did not finish loading (weak connection?).")}{" "}
          <button
            onClick={() => setHomeReloadKey((k) => k + 1)}
            style={{
              marginLeft: 8,
              padding: "6px 12px",
              borderRadius: 8,
              border: "1px solid #991b1b",
              background: "#fff",
              color: "#991b1b",
              fontWeight: 800,
              cursor: "pointer",
            }}
          >
            {t("Try again")}
          </button>
        </div>
      )}
      <div style={{ textAlign: "right", marginBottom: 10 }}>
  <LanguageToggle />
</div>
{/* ✅ STAFF-ONLY TOP NAV (Admin / Manager / Assistant) */}
{/* ✅ STAFF-ONLY TOP NAV (Admin / Manager / Assistant) */}
{isStaff && (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      gap: 10,
      marginBottom: 14,
      flexWrap: "wrap",
    }}
  >
    {/* ✅ ORDER: Home → Team Dashboard → Admin Portal */}

    <button
      onClick={() => navigate("/")}
      style={topNavBtnStyle("/")}
    >
      Home
    </button>

    <button
      onClick={() => navigate("/JobHistory")}
      style={topNavBtnStyle("/JobHistory")}
    >
      Team Dashboard
    </button>

    <button
      onClick={handleAdminPortalClick}
      style={topNavBtnStyle("/SupervisorDashboard")}
    >
      Admin Portal
    </button>

    {realUserRole === "Admin" && !isViewingAs() && (
      <button
        type="button"
        onClick={() => setShowViewAsPicker(true)}
        style={{ ...topNavBtnStyle("/__view_as"), background: "#fff7ed", border: "1px solid #c2410c", color: "#9a3412" }}
        title="See Home exactly as a tech sees it (read-only)"
      >
        👁 View as…
      </button>
    )}
  </div>
)}




<h1 className="center-text">{t("Welcome to the Portal")}</h1>
<p className="center-text">
  {t("Role")}: {userRole ? t(userRole) : ""}
</p>

<div
  style={{
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    marginBottom: 14,
  }}
>
  <label
    htmlFor="avatar-upload"
    style={{
      cursor: "pointer",
      display: "inline-flex",
      flexDirection: "column",
      alignItems: "center",
      gap: 8,
    }}
  >
    {currentUserData?.avatarURL ? (
      <img
        src={currentUserData.avatarURL}
        alt="Profile Avatar"
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          objectFit: "cover",
          border: "2px solid #1d4ed8",
        }}
      />
    ) : (
      <div
        style={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          background: "#dbeafe",
          border: "2px solid #93c5fd",
        }}
      />
    )}

    <span
      style={{
        fontSize: 13,
        fontWeight: 700,
        color: "#1d4ed8",
      }}
    >
      {uploadingAvatar ? t("Uploading...") : t("Click to change avatar")}
    </span>
  </label>

  <input
    id="avatar-upload"
    type="file"
    accept="image/*"
    onChange={handleAvatarUpload}
    style={{ display: "none" }}
  />
</div>

      <div style={{ marginBottom: 10, fontWeight: 600 }}>
      ⏱ {t("Today’s Hours")}: {clock.getTodaysHours()}
</div>

{/* Sub Contractors: what they've earned and been paid (shows only for them) */}
<MyPayPanel currentUser={currentUserData} />

    {/* ================= TECH HOME ================= */}
{isTech && (
  <>
    {/* CLOCK — always visible */}
    <ClockPanel clock={clock} techLocation={techLocation}t={t} />

    {/* JOB CHECK-IN — current stop + running timer */}
    <ActiveJobBanner jobVisits={jobVisits} isClockedIn={clock.isClockedIn} />
    <FenceAutoNotice fenceAuto={fenceAuto} />

    {/* TECH TAB HEADER */}
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 0,
  }}
>
  {/* LEFT: existing tabs */}
  <div style={{ display: "flex", gap: 8 }}>
  {/* Jobs */}
  <button
    onClick={() => setActiveTab("jobs")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "jobs" ? "1px solid white" : "1px solid #1d4ed8",
      background: activeTab === "jobs" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "jobs" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("Jobs")}
  </button>

  {/* Job Calendar */}
  <button
    onClick={() => setActiveTab("job-calendar")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "job-calendar"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background: activeTab === "job-calendar" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "job-calendar" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("Job Calendar")}
  </button>

  {/* Clock */}
  <button
    onClick={() => setActiveTab("clock")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "clock"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background: activeTab === "clock" ? "#1d4ed8" : "#93c5fd",
      color: activeTab === "clock" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("Clock In / Out")}
  </button>

  {/* Time Log Calendar */}
  <button
    onClick={() => setActiveTab("time-log-calendar")}
    style={{
      padding: "10px 14px",
      borderRadius: "10px 10px 0 0",
      border: "1px solid #1d4ed8",
      borderBottom:
        activeTab === "time-log-calendar"
          ? "1px solid white"
          : "1px solid #1d4ed8",
      background:
        activeTab === "time-log-calendar" ? "#1d4ed8" : "#93c5fd",
      color:
        activeTab === "time-log-calendar" ? "white" : "#0f172a",
      fontWeight: 700,
      cursor: "pointer",
    }}
  >
    {t("Time Log Calendar")}
  </button>

</div>

  <button
  onClick={() => setShowMoreMenu(true)}
  style={{
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    fontWeight: 700,
    cursor: "pointer",
    color: "#000",
  }}
>
  ⋯ {t("More")}
</button>

  
</div>


    {/* TECH TAB CONTENT */}
    {activeTab === "job-calendar" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <EmbeddedCalendar
  jobs={jobs}
  userRole={userRole}
  onBackToJobs={() => setActiveTab("jobs")}
  onJobUpdated={(updatedJob, result = null) => {
    if (updatedJob?.__deleted) {
      setJobs((previousJobs) =>
        previousJobs.filter((job) => job.id !== updatedJob.id)
      );
      setFilteredJobs((previousJobs) =>
        previousJobs.filter((job) => job.id !== updatedJob.id)
      );
      return;
    }

    if (result?.mode === "future" && result.oldJob && result.newJob) {
      setJobs((previousJobs) => [
        ...previousJobs.map((job) =>
          job.id === result.oldJob.id ? result.oldJob : job
        ),
        result.newJob,
      ]);
      return;
    }

    setJobs((previousJobs) =>
      previousJobs.map((job) =>
        job.id === updatedJob.id
          ? { ...job, ...updatedJob }
          : job
      )
    );

    setFilteredJobs((previousJobs) =>
      previousJobs.map((job) =>
        job.id === updatedJob.id
          ? { ...job, ...updatedJob }
          : job
      )
    );
  }}
  onAddSupervisorNote={addSupervisorNote}
  onContinueJob={(job) => {
    setSelectedJob(job);
    setContinueDate("");
    setShowContinuePopup(true);
  }}
  onDuplicateJob={(job) => setDuplicateJob(job)}
  onAddTotal={openTotalPopup}
  onAddReceipt={(job) => {
    setSelectedJob(job);
    setShowReceiptPopup(true);
  }}
  onChecklist={(job) =>
    navigate(`/JobChecklist/${job.id}`, {
      state: {
        returnTo: `${location.pathname}${location.search || ""}`,
        reopenJobId: job.id,
        reopenOccurrenceDate: job.occurrenceDate || job.startDate || "",
      },
    })
  }
/>

  </div>
)}
{activeTab === "time-log-calendar" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <ClockCalendar clock={clock} jobs={jobs} />
  </div>
)}

{activeTab === "clock-time" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <ClockTime />
  </div>
)}




{activeTab === "customer-codes" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <CustomerCodes />
  </div>
)}

{activeTab === "maps" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <Maps />
  </div>
)}

{activeTab === "time-off" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <TimeOff />
  </div>
)}

{activeTab === "warehouse" && (
  <div
    style={{
      border: "1px solid #1d4ed8",
      borderRadius: "0 10px 10px 10px",
      padding: 16,
      background: "#fff",
      marginBottom: 20,
    }}
  >
    <WarehouseInventory embedded />
  </div>
)}

{activeTab === "jobs" && (
  <>
    <h2 style={{ marginTop: 20 }}>
      {techJobsTitle}
    </h2>

    <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
      <button
        style={chip("#93c5fd", techDateView === "today")}
        onClick={() => setTechDateView("today")}
      >
        {t("Today")}
      </button>

      <button
        style={chip("#facc15", techDateView === "tomorrow")}
        onClick={() => setTechDateView("tomorrow")}
      >
        {t("Tomorrow")}
      </button>

      <button
        style={chip("#4ade80", techDateView === "week")}
        onClick={() => setTechDateView("week")}
      >
        {t("This Week")}
      </button>
    </div>

    {techUpcomingJobs.length === 0 ? (
      <p style={{ fontStyle: "italic" }}>{t("No jobs scheduled for this time frame.")}</p>
    ) : (
      techUpcomingJobs.map((job) => {
        const materialsCount = getMaterialsForJob(job).length;
        const timeWindow =
          job.startTimeMode === "custom"
            ? t("Custom ({time})", { time: job.startTime || t("N/A") })
            : job.startTime === "ANY" || !job.startTime
            ? t("ANY")
            : job.startTime;
        return (
        <div
          key={job.id + "_" + (job.occurrenceDate || job.startDate || "")}
          className="tjt-card"
        >
          <div className="tjt-head">
            <div className="tjt-site">{job.siteName}</div>
            <span className="tjt-wo">{t("WO #")}{job.wo || "—"}</span>
          </div>

          <div className="tjt-sub">
            <span className="tjt-service">
              {job.serviceType ? (
                <TranslatedText text={job.serviceType} showToggle={false} />
              ) : (
                t("No Service Type")
              )}
            </span>
            {job.city && <span>📍 {job.city}</span>}
            {job.clientWorkOrder && (
              <span>
                {t("Client WO #")}
                {job.clientWorkOrder}
              </span>
            )}
          </div>

          {job.scope && (
            <div className="tjt-scope">
              <TranslatedText text={job.scope} />
            </div>
          )}

          <div className="tjt-meta">
            <div className="tjt-meta-item">
              <span className="tjt-meta-label">{t("Date")}</span>
              {formatTileDate(job.occurrenceDate || job.startDate) || t("No date")}
            </div>
            <div className="tjt-meta-item">
              <span className="tjt-meta-label">{t("Time Window")}</span>
              {timeWindow}
            </div>
            <div className="tjt-meta-item">
              <span className="tjt-meta-label">{t("Company")}</span>
              {job.companyUse || t("N/A")}
            </div>
          </div>

          {materialsCount > 0 && (
            <div className="tjt-materials">
              🧱 {t("Materials required ({count})", { count: materialsCount })}
            </div>
          )}

          {/* Check In / View Job / Start-Resume: three equal buttons */}
          <div className="tjt-actions">
            <JobCheckInButton job={job} jobVisits={jobVisits} />

            <button type="button" className="tjt-btn tjt-btn-view" onClick={() => setSelectedJob(job)}>
              {t("View Job")}
            </button>

            <button
              type="button"
              className="tjt-btn tjt-btn-start"
              onClick={() => {
                setSelectedJob(job);
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (pos) => {
                      setTechLocation({
                        lat: pos.coords.latitude,
                        lng: pos.coords.longitude,
                      });
                    },
                    (err) => console.warn("Location error:", err),
                    { enableHighAccuracy: true }
                  );
                }
              }}
            >
              {t("Start / Resume")}
            </button>
          </div>
        </div>
        );
      })
    )}
  </>
)}

  </>
)}
{/* ================= ADMIN / MANAGER / ASSISTANT HOME ================= */}
{isStaff && (
  <>
    {/* 🚨 Jobs whose date passed without being completed */}
    <MissedJobsBanner count={missedRows.length} onOpen={openMissedJobs} />
    {/* 👷 ⏱ 💵 Job reminders - each opens its own popup */}
    {REMINDER_ORDER.map((kind) => (
      <JobReminderBanner
        key={kind}
        kind={kind}
        count={homeReminders.rows[kind]?.length || 0}
        onOpen={() => openReminder(kind)}
      />
    ))}
    {/* Jobs techs marked completed, waiting to be finished + invoiced */}
    <TechCompletionQueue
      jobs={jobs}
      staffName={userFullName}
      onOpenJob={(job) => {
        setIsEditing(false);
        setSelectedJob(job);
      }}
    />
    {/* Step 2: Tab Header */}
    <div
  style={{
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    gap: 12,
    marginBottom: 0,
  }}
>
  {/* LEFT: existing tabs */}
  <div style={{ display: "flex", gap: 8 }}>
    {["jobs", "job-calendar", "clock", "time-log-calendar"].map((tKey) => (
      <button
        key={tKey}
        onClick={() => setActiveTab(tKey)}
        style={{
          padding: "10px 14px",
          borderRadius: "10px 10px 0 0",
          border: "1px solid #1d4ed8",
          borderBottom:
            activeTab === tKey ? "1px solid white" : "1px solid #1d4ed8",
          background: activeTab === tKey ? "#1d4ed8" : "#93c5fd",
          color: activeTab === tKey ? "white" : "#0f172a",
          fontWeight: 700,
          cursor: "pointer",
        }}
      >
        {tKey === "jobs" && "Jobs"}
        {tKey === "job-calendar" && "Job Calendar"}
        {tKey === "clock" && "Clock In / Out"}
{tKey === "time-log-calendar" && "Time Log Calendar"}

      </button>
    ))}
  </div>
  <button
  onClick={() => setShowMoreMenu(true)}
  style={{
    padding: "10px 12px",
    borderRadius: "10px 10px 0 0",
    border: "1px solid #1d4ed8",
    background: "#e0e7ff",
    fontWeight: 700,
    cursor: "pointer",
    color: "#000",
  }}
>
  ⋯ {t("More")}
</button>

</div>


    {/* Step 3: Container Wrapper */}
    <div
      style={{
        border: "1px solid #1d4ed8",
        borderRadius: "0 10px 10px 10px",
        padding: 16,
        background: "#fff",
      }}
    >

    {isStaff && activeTab === "jobs" && (
  <>
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 8,
        marginBottom: 10,
        flexWrap: "wrap",
      }}
    >
      <button
  type="button"
  onClick={() => {
    const nextOpen = !showTeamMessaging;
    setShowTeamMessaging(nextOpen);
  
    if (nextOpen) {
      handleViewedTeamMessaging();
    }
  }}
  style={getTeamMessagingButtonStyle()}
>
<span>
  {showTeamMessaging ? "Hide Direct / Group Message" : "Show Direct / Group Message"}
  {teamUnreadLabels.length > 0
    ? ` (${teamUnreadLabels.slice(0, 2).join(", ")}${teamUnreadLabels.length > 2 ? "..." : ""})`
    : ""}
</span>

  {teamHasUnread && (
    <span
      style={{
        minWidth: 10,
        height: 10,
        borderRadius: "50%",
        background: "#dc2626",
        display: "inline-block",
      }}
      title="New team activity"
    />
  )}
</button>

      <button
        type="button"
        onClick={() => setShowMaterialsNeeded((p) => !p)}
        style={{
          padding: "8px 12px",
          borderRadius: 10,
          border: "1px solid #1d4ed8",
          background: showMaterialsNeeded ? "#fee2e2" : "#e0e7ff",
          color: "#0f172a",
          fontWeight: 900,
          cursor: "pointer",
        }}
      >
        {showMaterialsNeeded
          ? "Hide Materials Needed for Jobs"
          : "Show Materials Needed for Jobs"}
      </button>
    </div>

    <TeamMessaging
  userId={userId}
  userFullName={userFullName}
  isOpen={showTeamMessaging}
  onViewed={handleViewedTeamMessaging}
  openTaskCount={teamOpenTaskCount}
  usersList={usersList}
/>

    {showMaterialsNeeded && (
      <div
        style={{
          border: "1px solid #1d4ed8",
          borderRadius: 10,
          padding: 12,
          marginBottom: 14,
          background: "#f8fafc",
        }}
      >

   <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
  <h3 style={{ marginBottom: 6 }}>🧱 Materials Needed for Jobs</h3>

  <select
    value={materialsWeeksAhead}
    onChange={(e) => setMaterialsWeeksAhead(Number(e.target.value))}
    style={{
      padding: "6px 10px",
      borderRadius: 10,
      border: "1px solid rgba(0,0,0,0.2)",
      fontWeight: 800,
    }}
  >
    <option value={1}>This Week</option>
    <option value={4}>Next 4 Weeks</option>
  </select>
</div>


    {weeklyMaterials.length === 0 ? (
      <p style={{ fontStyle: "italic", color: "#64748b" }}>
        No materials required this week.
      </p>
    ) : (
      <ul style={{ paddingLeft: 0, marginTop: 8, listStyle: "none" }}>
      {weeklyMaterials.map((m, i) => {
        const key = `${m.jobId}_${m.materialIndex}`;
        const ordered = !!m.ordered;
        const received = !!m.received;
    
        return (
          <li
            key={key}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              padding: "8px 10px",
              border: "1px solid rgba(29,78,216,0.25)",
              borderRadius: 10,
              marginBottom: 8,
              background: received ? "#dcfce7" : ordered ? "#fef9c3" : "#fff",
            }}
          >
            {/* LEFT: Material info */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 800 }}>
                {m.siteName} <span style={{ fontWeight: 700, color: "#334155" }}>(WO #{m.wo || "—"})</span>
              </div>
    
              <div style={{ color: "#0f172a" }}>
                {m.qty} {m.unit} {m.name}
                {m.notes && <span style={{ color: "#475569" }}> — {m.notes}</span>}
              </div>
    
              {/* Status line */}
              <div style={{ fontSize: 12, color: "#475569", marginTop: 2 }}>
                {received
                  ? `✅ Received${m.receivedLocation ? ` (${m.receivedLocation})` : ""}`
                  : ordered
                  ? "🟨 Ordered"
                  : "⬜ Not ordered"}
              </div>
            </div>
    
            {/* RIGHT: Buttons */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              {/* ORDERED toggle */}
              <button
                type="button"
                onClick={async (e) => {
                  e.stopPropagation();
                  await updateMaterialStatus(m.jobId, m.materialIndex, {
                    ordered: !ordered,
                    orderedAt: !ordered ? new Date().toISOString() : null,
                    // if un-ordering, also clear received
                    ...(ordered ? { received: false, receivedAt: null, receivedLocation: "" } : {}),
                  });
                  setOpenReceivedKey(null);
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "1px solid #1d4ed8",
                  background: ordered ? "#facc15" : "#e0e7ff",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Ordered
              </button>
    
              {/* RECEIVED button */}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // open dropdown next to this row
                  setOpenReceivedKey((prev) => (prev === key ? null : key));
                  // default draft selection
                  setReceivedLocationDraft((prev) => ({
                    ...prev,
                    [key]: prev[key] || m.receivedLocation || "Shop 1",
                  }));
                }}
                style={{
                  padding: "6px 10px",
                  borderRadius: 10,
                  border: "1px solid #16a34a",
                  background: received ? "#86efac" : "#dcfce7",
                  fontWeight: 800,
                  cursor: "pointer",
                }}
              >
                Received
              </button>
    
              {/* Dropdown appears next to button */}
              {openReceivedKey === key && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <select
                    value={receivedLocationDraft[key] || "Shop 1"}
                    onChange={(e) =>
                      setReceivedLocationDraft((prev) => ({ ...prev, [key]: e.target.value }))
                    }
                    style={{
                      padding: "6px 8px",
                      borderRadius: 10,
                      border: "1px solid rgba(0,0,0,0.2)",
                      fontWeight: 700,
                    }}
                  >
                    {LOCATION_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
    
                  <button
                    type="button"
                    onClick={async (e) => {
                      e.stopPropagation();
                      const loc = receivedLocationDraft[key] || "Shop 1";
    
                      await updateMaterialStatus(m.jobId, m.materialIndex, {
                        ordered: true, // receiving implies ordered
                        orderedAt: m.orderedAt || new Date().toISOString(),
                        received: true,
                        receivedAt: new Date().toISOString(),
                        receivedLocation: loc,
                      });
    
                      setOpenReceivedKey(null);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: "1px solid #16a34a",
                      background: "#16a34a",
                      color: "white",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    Save
                  </button>
    
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setOpenReceivedKey(null);
                    }}
                    style={{
                      padding: "6px 10px",
                      borderRadius: 10,
                      border: "1px solid #dc2626",
                      background: "#fee2e2",
                      color: "#991b1b",
                      fontWeight: 900,
                      cursor: "pointer",
                    }}
                  >
                    X
                  </button>
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ul>
    
    )}
       </div>
    )}
  </>
)}


      {/* ================= TAB CONTENT ================= */}
      {activeTab === "jobs" && (
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
  <div
    style={chip("#ffebee", dateFilter === "overdue")}
    onClick={() => setDateFilter("overdue")}
  >
    🚨 Missed: {summary.overdue}
  </div>
  <div
  style={chip("#e0f2fe", dateFilter === "today")}
  onClick={() => setDateFilter("today")}
>
  📍 Due Today: {summary.dueToday}
</div>

  <div
    style={chip("#fff3cd", dateFilter === "tomorrow")}
    onClick={() => setDateFilter("tomorrow")}
  >
    📅 Due Tomorrow: {summary.dueTomorrow}
  </div>

  <div
    style={chip("#e3f2fd", dateFilter === "week")}
    onClick={() => setDateFilter("week")}
  >
    🗓 Due This Week: {summary.dueThisWeek}
  </div>

  <div
    style={chip("#dbeafe", dateFilter === "nextWeek")}
    onClick={() => setDateFilter("nextWeek")}
  >
    📆 Next Week: {summary.dueNextWeek}
  </div>

  <div
  style={chip("#f3e5f5", dateFilter === "needsScheduling")}
  onClick={() => setDateFilter("needsScheduling")}
>
  🧩 Needs Scheduling: {summary.needsScheduling}
</div>

<div
  style={chip("#e0e7ff", dateFilter === "allActive")}
  onClick={() => {
    setDateFilter("allActive");
    setSelectedStartDate(""); // ✅ clears date picker
  }}
>
  📋 All Jobs: {summary.allActive}
</div>



  <button
  onClick={() => {
    setDateFilter("");
    setSelectedStartDate("");
    setSelectedTech("ALL_TECHS");
  }}
  style={{
    padding: "6px 12px",
    borderRadius: 16,
    border: "1px solid #dc2626",
    background: "#fee2e2",
    color: "#991b1b",
    fontWeight: 700,
    cursor: "pointer",
  }}
>
  ✖ Clear Filters
</button>

</div>
      )}


      {activeTab === "jobs" && (
        <>
      {isAdmin && (
  <div
    style={{
      display: "flex",
      gap: 8,
      flexWrap: "wrap",
      marginBottom: 12,
      alignItems: "center",
    }}
  >
    <button
      type="button"
      onClick={selectAllFilteredJobsForExport}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #1d4ed8",
        background: "#e0e7ff",
        color: "#1d4ed8",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      Select All Filtered
    </button>

    <button
      type="button"
      onClick={clearSelectedJobsForExport}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #dc2626",
        background: "#fee2e2",
        color: "#991b1b",
        fontWeight: 700,
        cursor: "pointer",
      }}
    >
      Clear Selected
    </button>

    <button
      type="button"
      onClick={exportSelectedJobsToExcel}
      style={{
        padding: "8px 12px",
        borderRadius: 8,
        border: "1px solid #16a34a",
        background: "#dcfce7",
        color: "#166534",
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      Export Selected to Excel
    </button>

    <span style={{ fontWeight: 700 }}>
      Selected: {selectedExportJobIds.length}
    </span>
  </div>
)}
  {/* 🔍 Search Bar */}
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
  <input
    type="text"
    placeholder="Search by Site, City, State, or Scope…"
    value={searchTerm}
    onChange={handleSearchChange}
    style={{
      padding: "8px 12px",
      borderRadius: 8,
      border: "1px solid #1d4ed8",
      minWidth: 280,
    }}
  />

<select
  value={selectedTech}
  onChange={(e) => {
    const value = e.target.value;

    // ✅ set tech first
    setSelectedTech(value);

    // ✅ if picking a specific tech, clear anything that could "clash"
    if (value !== "ALL_TECHS") {
      setDateFilter("allActive");     // or "" if you prefer no chip active
      setSelectedStartDate("");
      setSelectedSite("ALL_SITES");
      setSortOption("");
      setSearchTerm("");              // optional, but matches your "clear other filters" goal
    }
  }}
>

    <option value="ALL_TECHS">All Technicians</option>
    {dropdownUsers
    .filter(u =>
      ["Tech", "Manager", "Assistant", "Admin"].includes(u.role)
    )
    .map(u => (
      <option
        key={u.id}
        value={`${u.firstName} ${u.lastName}`}
      >
        {u.firstName} {u.lastName} ({u.role})
      </option>
    ))}
</select>
</div>


{isAdmin && pipeline && (
  <div className="pipeline-panel">
    <button
      type="button"
      className="pipeline-panel__head"
      onClick={() => setPipelineOpen((v) => !v)}
    >
      <span className="pipeline-panel__title">
        Pipeline <span className="pipeline-panel__lock">Admin only</span>
      </span>
      <span className="pipeline-panel__chev">{pipelineOpen ? "Hide" : "Show"}</span>
    </button>

    {pipelineOpen && (
      <div className="pipeline-panel__body">
        <div className="pipeline-panel__presets">
          {PIPELINE_RANGES.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`pipeline-panel__preset${pipelineRange === r.id ? " is-active" : ""}`}
              onClick={() => setPipelineRange(r.id)}
            >
              {r.label}
            </button>
          ))}
        </div>

        {pipelineRange === "custom" && (
          <div className="pipeline-panel__custom">
            <label>
              From
              <input type="date" value={pipelineFrom}
                onChange={(e) => setPipelineFrom(e.target.value)} />
            </label>
            <label>
              To
              <input type="date" value={pipelineTo}
                onChange={(e) => setPipelineTo(e.target.value)} />
            </label>
          </div>
        )}

        <div className="pipeline-panel__figures">
          <div className="pipeline-panel__figure">
            <span className="pipeline-panel__label">Active jobs</span>
            <span className="pipeline-panel__value">{pipeline.count}</span>
          </div>
          <div className="pipeline-panel__figure">
            <span className="pipeline-panel__label">Potential total</span>
            <span className="pipeline-panel__value pipeline-panel__value--money">
              ${pipeline.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          </div>
        </div>

        <div className="pipeline-panel__foot">
          <span className="pipeline-panel__range">
            {pipeFrom === pipeTo
              ? prettyRangeDay(pipeFrom)
              : `${prettyRangeDay(pipeFrom)} \u2013 ${prettyRangeDay(pipeTo)}`}
          </span>
          {pipeline.days.length > 0 && (
            <button type="button" className="pipeline-panel__toggle"
              onClick={() => setPipelineBreakdown((v) => !v)}>
              {pipelineBreakdown ? "Hide daily breakdown" : "Daily breakdown"}
            </button>
          )}
        </div>

        {pipelineBreakdown && pipeline.days.length > 0 && (
          <table className="pipeline-panel__table">
            <thead>
              <tr><th>Day</th><th>Jobs</th><th>Total</th></tr>
            </thead>
            <tbody>
              {pipeline.days.map((row) => (
                <tr key={row.day}>
                  <td>{prettyRangeDay(row.day)}</td>
                  <td>{row.count}</td>
                  <td>${row.amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pipeline.count === 0 && (
          <p className="pipeline-panel__empty">No active jobs starting in this range.</p>
        )}

        {pipeline.missing > 0 && (
          <p className="pipeline-panel__note">
            {pipeline.missing} of these {pipeline.missing === 1 ? "has" : "have"} no total entered,
            so {pipeline.missing === 1 ? "it counts" : "they count"} as $0. The real figure is higher.
          </p>
        )}

        {pipeline.unscheduled > 0 && (
          <p className="pipeline-panel__note">
            {pipeline.unscheduled} active job{pipeline.unscheduled === 1 ? " has" : "s have"} no start
            date, so {pipeline.unscheduled === 1 ? "it is" : "they are"} not in any range above.
          </p>
        )}
      </div>
    )}
  </div>
)}

{filteredJobs.length === 0 ? (
  <p>No jobs match the selected filters.</p>
) : (
  filteredJobs.map((job) => {
    const completed = isJobCompleted(job);
  
    return (
      <div
        key={`${job.id}_${job.occurrenceDate || toDateKey(job.startDate)}`}
        style={{
          ...jobRowStyle(job),
          position: "relative",
          paddingLeft: isAdmin ? 34 : 10,
        }}
        onClick={() => setSelectedJob(job)}
      >
        {isAdmin && (
          <input
            type="checkbox"
            checked={selectedExportJobIds.includes(getJobExportKey(job))}
            onClick={(e) => e.stopPropagation()}
            onChange={() => toggleExportJob(getJobExportKey(job))}
            style={{
              position: "absolute",
              top: 10,
              left: 10,
              margin: 0,
              width: 14,
              height: 14,
              cursor: "pointer",
            }}
          />
        )}
    
        <div>
          <strong>{job.siteName}</strong>
    
          <div style={{ fontSize: 12, fontWeight: 800, color: "#0f172a", marginTop: 2 }}>
  WO #{job.wo || "—"}
</div>

<div style={{ fontSize: 12, fontWeight: 800, color: "#334155", marginTop: 2 }}>
  Client WO #{job.clientWorkOrder || "—"}
</div>

<div style={{ fontSize: 12, fontWeight: 700, color: "#1d4ed8", marginTop: 2 }}>
  {job.serviceType || "No Service Type"}
</div>
    
          {(() => {
            const mismatch = getDurationMismatch(job);
            return mismatch && (
              <div style={{ color: "#b45309", fontSize: 12, fontWeight: 700 }}>
                ⚠️ Duration mismatch: scheduled {mismatch.scheduledDays}d / planned {mismatch.estimatedDays}d
              </div>
            );
          })()}
    
          {job.scope && (
            <span style={{ marginLeft: 6, color: "#475569", fontStyle: "italic" }}>
              ({job.scope})
            </span>
          )}{" "}
          — {job.city}
    
          <br />
    
          <small>
  {job.startDate
    ? job.endDate && job.endDate !== job.startDate
      ? `${job.startDate} → ${job.endDate}`
      : job.startDate
    : "No date"}
</small>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Time Window:</strong>{" "}
  {job.startTimeMode === "custom"
    ? `Custom (${job.startTime || "N/A"})`
    : job.startTime || "ANY"}
</div>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Company:</strong> {job.companyUse || "N/A"}
</div>

<div style={{ fontSize: 12, color: "#334155", marginTop: 2 }}>
  <strong>Tech:</strong>{" "}
  {Array.isArray(job.techAssign)
    ? job.techAssign.join(", ")
    : job.techAssign || "Unassigned"}
</div>
        </div>
      </div>
    );
  })
  
)}


        </>
      )}

      {activeTab === "clock" && <ClockPanel clock={clock} t={t} />}
      {activeTab === "clock" && (
        <>
          <ActiveJobBanner jobVisits={jobVisits} isClockedIn={clock.isClockedIn} />
          <FenceAutoNotice fenceAuto={fenceAuto} />
        </>
      )}

      {!isTech && activeTab === "job-calendar" && (
        <EmbeddedCalendar
          jobs={jobs}
          userRole={userRole}
          onBackToJobs={() => setActiveTab("jobs")}
          onJobUpdated={(updatedJob) => {
            if (!updatedJob?.id) return;
            if (updatedJob.__deleted) {
              setJobs((prev) => prev.filter((job) => job.id !== updatedJob.id));
              return;
            }
            setJobs((prev) => {
              const exists = prev.some((job) => job.id === updatedJob.id);
              return exists
                ? prev.map((job) => job.id === updatedJob.id ? { ...job, ...updatedJob } : job)
                : [...prev, updatedJob];
            });
          }}
          onAddSupervisorNote={addSupervisorNote}
          onContinueJob={(job) => {
            setSelectedJob(job);
            setContinueDate("");
            setShowContinuePopup(true);
          }}
          onDuplicateJob={(job) => setDuplicateJob(job)}
          onAddTotal={openTotalPopup}
          onAddReceipt={(job) => {
            setSelectedJob(job);
            setShowReceiptPopup(true);
          }}
          onChecklist={(job) =>
            navigate(`/JobChecklist/${job.id}`, {
              state: {
                returnTo: `${location.pathname}${location.search || ""}`,
                reopenJobId: job.id,
                reopenOccurrenceDate: job.occurrenceDate || job.startDate || "",
              },
            })
          }
        />
      )}

{activeTab === "time-log-calendar" && (
  <ClockCalendar clock={clock} jobs={jobs} />
)}     
{activeTab === "customer-codes" && <CustomerCodes />}

{activeTab === "maps" && <Maps />}

{activeTab === "time-off" && <TimeOff />}

{activeTab === "warehouse" && <WarehouseInventory embedded />}

{activeTab === "clock-time" && <ClockTime />}


    
    </div>
  </>
)}


      {selectedJob && (
        <>
          <div className="modal-overlay" onClick={closeModal}></div>
          <div className="modal jdp">
            <JobDetailHeader
              job={selectedJob}
              t={t}
              onClose={closeModal}
              eyebrow={isEditing ? t("Edit Job") : t("Job Details")}
            />
            <div className="jdp-body">

            {!isEditing && ["Admin", "Manager", "Assistant"].includes(userRole) && (
              <QuickJobFieldEditor
                job={selectedJob}
                usersList={usersList}
                onSave={saveQuickJobFields}
              />
            )}

            {isEditing ? (
              <form className="form-container" onSubmit={handleSaveChanges}>
                <label>Site Name:</label>
                <input
                  type="text"
                  name="siteName"
                  value={editedJob.siteName || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      siteName: e.target.value,
                    }))
                  }
                />

<label style={{ display: "flex", gap: 8, alignItems: "center" }}>
  <input
    type="checkbox"
    checked={editedJob.isProject === true}
    onChange={(e) =>
      setEditedJob((prev) => ({
        ...prev,
        isProject: e.target.checked,
        projectStatus: e.target.checked
          ? prev.projectStatus || "Active"
          : "",
        projectRootId: e.target.checked
          ? prev.projectRootId || prev.id || selectedJob.id
          : "",
        projectDayNumber: e.target.checked
          ? Number(prev.projectDayNumber || 1)
          : "",
      }))
    }
  />
  Project Job
</label>

{editedJob.isProject && (
  <div
    style={{
      padding: 10,
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      borderRadius: 8,
      marginBottom: 10,
      fontWeight: 700,
    }}
  >
    Project is active. Daily completion will create the next day automatically.
  </div>
)}

<label>Client Work Order #:</label>
<input
  type="text"
  name="clientWorkOrder"
  value={editedJob.clientWorkOrder || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientWorkOrder: e.target.value,
    }))
  }
/>

                <label>Address:</label>
                <input
                  type="text"
                  name="address"
                  value={editedJob.address || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      address: e.target.value,
                    }))
                  }
                />

                <label>City:</label>
                <input
                  type="text"
                  name="city"
                  value={editedJob.city || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      city: e.target.value,
                    }))
                  }
                />

                <label>State:</label>
                <input
                  type="text"
                  name="state"
                  value={editedJob.state || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      state: e.target.value,
                    }))
                  }
                />

                <label>Start Date:</label>
                <input
                  type="date"
                  name="startDate"
                  value={editedJob.startDate || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      startDate: e.target.value,
                    }))
                  }
                />

                <label>End Date:</label>
                <input
                  type="date"
                  name="endDate"
                  value={editedJob.endDate || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      endDate: e.target.value,
                    }))
                  }
                />
                <h4 style={{ marginTop: 16 }}>Job Planning</h4>

<label>Estimated Days</label>
<input
  type="number"
  min="1"
  value={editedJob.jobPlan?.estimatedDays || ""}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedDays: Number(e.target.value),
      },
    }))
  }
/>

<label>Estimated Labor (people)</label>
<input
  type="number"
  min="1"
  value={editedJob.jobPlan?.estimatedLabor || ""}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      jobPlan: {
        ...prev.jobPlan,
        estimatedLabor: Number(e.target.value),
      },
    }))
  }
/>

                <label>Recurrence:</label>
<select
  value={editedJob.recurrence?.type || "none"}
  onChange={(e) =>
    setEditedJob(prev => ({
      ...prev,
      recurrence: {
        ...prev.recurrence,
        type: e.target.value,
      },
    }))
  }
>
  <option value="none">One-Time</option>
  <option value="daily">Daily</option>
  <option value="weekly">Weekly</option>
  <option value="biweekly">Bi-Weekly</option>
  <option value="monthly">Monthly</option>
  <option value="bimonthly">Bi-Monthly</option>
  <option value="quarterly">Quarterly</option>
  <option value="biyearly">Bi-Yearly</option>
  <option value="yearly">Yearly</option>
  <option value="customDates">Custom Dates</option>
</select>

{editedJob.recurrence?.type === "weekly" && (
  <>
    <label>Days of Week:</label>
    {[0,1,2,3,4,5,6].map(d => (
      <label key={d} style={{ marginRight: 8 }}>
        <input
          type="checkbox"
          checked={editedJob.recurrence?.daysOfWeek?.includes(d)}
          onChange={(e) => {
            const days = editedJob.recurrence.daysOfWeek || [];
            const updated = e.target.checked
              ? [...days, d]
              : days.filter(x => x !== d);

            setEditedJob(prev => ({
              ...prev,
              recurrence: {
                ...prev.recurrence,
                daysOfWeek: updated,
              },
            }));
          }}
        />
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][d]}
      </label>
    ))}
  </>
)}

{editedJob.recurrence?.type === "customDates" && (
  <>
    <label>Custom Dates (comma separated):</label>
    <input
      type="text"
      value={(editedJob.recurrence.customDates || []).join(",")}
      onChange={(e) =>
        setEditedJob(prev => ({
          ...prev,
          recurrence: {
            ...prev.recurrence,
            customDates: e.target.value
              .split(",")
              .map(d => d.trim())
              .filter(Boolean),
          },
        }))
      }
    />
  </>
)}


<label>Start Time:</label>
<select
  name="startTime"
  value={editedJob.startTime ?? ""}
  onChange={(e) => {
    const value = e.target.value;

    if (value !== "Custom Time") {
      setEditedJob((prev) => ({
        ...prev,
        startTime: value,
        startTimeMode: "preset",
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        startTime: "",
        startTimeMode: "custom",
      }));
    }
  }}
>
  <option value="">Select Start Time</option>
  {timeWindowOptions.map((opt) => (
    <option key={opt} value={opt}>{opt}</option>
  ))}
</select>

{/* Show only when Custom Time is selected */}
{editedJob.startTimeMode === "custom" && (
  <input
    type="time"
    value={editedJob.startTime || ""}
    onChange={(e) =>
      setEditedJob((prev) => ({ ...prev, startTime: e.target.value }))
    }
  />
)}

<label>End Time:</label>
<input
  type="time"
  name="endTime"
  value={editedJob.endTime || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({ ...prev, endTime: e.target.value }))
  }
/>

<label>Service Type:</label>
<select
  name="serviceType"
  value={editedJob.serviceType || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      serviceType: e.target.value,
    }))
  }
>
  <option value="">Select Service Type</option>
  {withCurrentValue(serviceTypes, editedJob.serviceType).map((t) => (
    <option key={t} value={t}>
      {t}
    </option>
  ))}
</select>
<label>Select Company:</label>
<select
  name="companyUse"
  value={
    ["JMC", "DCS", "OGR", "MHI", "Other"].includes(editedJob.companyUseMode)
      ? editedJob.companyUseMode
      : (
          ["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse)
            ? editedJob.companyUse
            : (editedJob.companyUse ? "Other" : "")
        )
  }
  onChange={(e) => {
    const value = e.target.value;

    if (value === "Other") {
      setEditedJob((prev) => ({
        ...prev,
        companyUseMode: "Other",
        companyUse:
          ["JMC", "DCS", "OGR", "MHI"].includes(prev.companyUse)
            ? ""
            : (prev.companyUse || ""),
      }));
    } else {
      setEditedJob((prev) => ({
        ...prev,
        companyUseMode: value,
        companyUse: value,
      }));
    }
  }}
>
  <option value="">Select Company</option>
  <option value="JMC">JMC</option>
  <option value="DCS">DCS</option>
  <option value="OGR">OGR</option>
  <option value="MHI">MHI</option>
  <option value="Other">Other</option>
</select>

{editedJob.companyUseMode === "Other" && (
  <>
    <label>Other Company Name:</label>
    <input
      type="text"
      value={
        ["JMC", "DCS", "OGR", "MHI"].includes(editedJob.companyUse)
          ? ""
          : (editedJob.companyUse || "")
      }
      onChange={(e) =>
        setEditedJob((prev) => ({
          ...prev,
          companyUse: e.target.value,
          companyUseMode: "Other",
        }))
      }
      placeholder="Enter company name"
    />
  </>
)}

                <label>Scope:</label>
                <input
                  type="text"
                  name="scope"
                  value={editedJob.scope || ""}
                  onChange={(e) =>
                    setEditedJob((prev) => ({
                      ...prev,
                      scope: e.target.value,
                    }))
                  }
                />

<label>Job Description:</label>
<textarea
  name="jobDescription"
  value={editedJob.jobDescription || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      jobDescription: e.target.value,
    }))
  }
  rows={8}
  style={{ resize: "vertical" }}
/>

<label style={{ marginTop: 10 }}>Job Description Preview:</label>
<div
  style={{
    marginTop: 8,
    padding: "10px 12px",
    border: "1px solid #d1d5db",
    borderRadius: 8,
    background: "#f9fafb",
    whiteSpace: "normal",
  }}
>
  {parseFormattedJobDescription(editedJob.jobDescription)}
</div>

<label>Client Phone:</label>
<input
  type="text"
  name="clientPhone"
  value={editedJob.clientPhone || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientPhone: e.target.value,
    }))
  }
/>

<label>Client Email:</label>
<input
  type="email"
  name="clientEmail"
  value={editedJob.clientEmail || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      clientEmail: e.target.value,
    }))
  }
/>


<label>Tech Assign:</label>

{(editedJob.techAssign || []).map((tech, index) => (
  <div
    key={index}
    style={{
      display: "flex",
      alignItems: "center",
      gap: "8px",
      marginBottom: "8px",
    }}
  >
    <select
      value={tech}
      onChange={(e) => {
        const updated = [...(editedJob.techAssign || [])];
        updated[index] = e.target.value;
        setEditedJob((prev) => ({
          ...prev,
          techAssign: updated,
        }));
      }}
      style={{ flex: 1 }}
    >
      <option value="">Select Technician</option>
      {dropdownUsers.map((u) => (
        <option key={u.id} value={`${u.firstName} ${u.lastName}`}>
          {u.firstName} {u.lastName}
        </option>
      ))}
    </select>

    <button
      type="button"
      className="red-button"
      onClick={() =>
        setEditedJob((prev) => ({
          ...prev,
          techAssign: (prev.techAssign || []).filter(
            (_, i) => i !== index
          ),
        }))
      }
      style={{
        width: "44px",
        minWidth: "44px",
        maxWidth: "44px",
        height: "44px",
        minHeight: "44px",
        padding: 0,
        fontSize: "18px",
        fontWeight: "bold",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
      title="Remove technician"
    >
      X
    </button>
  </div>
))}

<button
  type="button"
  className="blue-button"
  onClick={() =>
    setEditedJob((prev) => ({
      ...prev,
      techAssign: [...(prev.techAssign || []), ""],
    }))
  }
>
  Add Technician
</button>

{/* Daily / Project pay for Sub Contractors on this job (Admin only) */}
<SubPayTermsInline
  job={editedJob}
  users={usersList}
  userRole={userRole}
  onChange={(patch) => setEditedJob((prev) => ({ ...prev, ...patch }))}
/>



<label>Status:</label>
<select
  name="status"
  value={editedJob.status || ""}
  onChange={(e) =>
    setEditedJob((prev) => ({
      ...prev,
      status: e.target.value,
    }))
  }
>
  <option value="">Select Status</option>
  {statusOptions.map((s) => (
    <option key={s} value={s}>
      {s}
    </option>
  ))}
</select>


                <button
                  type="button"
                  className="blue-button"
                  onClick={handleSaveChanges}
                >
                  Save
                </button>
                <button
                  type="button"
                  className="red-button"
                  onClick={() => setIsEditing(false)}
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                {isTech && <TechCompletionStatus request={techCompletionRequest} />}
                {/* Job check-in: techs always; anyone else when assigned to this job */}
                {(isTech || isAssignedToMe(selectedJob)) && (
                  <JobCheckInButton job={selectedJob} jobVisits={jobVisits} />
                )}
                {/* Opening a job you should be at today asks if you are on site. */}
                <JobOpenCheckInPrompt
                  job={selectedJob}
                  jobVisits={jobVisits}
                  eligible={isTech || isAssignedToMe(selectedJob)}
                  isClockedIn={clock.isClockedIn}
                />
                {/* Staff: who checked in here, when, and for how long */}
                {isStaff && <JobVisitHistory job={selectedJob} />}
                {/* Staff: confirm where this job is for automatic check-in */}
                {isStaff && (
                  <JobFenceStatus
                    job={selectedJob}
                    canEdit
                    onSaved={(jobFence) => applyJobFence(selectedJob.id, jobFence)}
                  />
                )}
                <DetailFacts
                  items={[
                    { label: t("Site Name:"), value: selectedJob.siteName },
                    { label: t("Work Order:"), value: selectedJob.wo },
                    { label: t("Company:"), value: selectedJob.companyUse || t("N/A") },
                    { label: t("Client Work Order #:"), value: selectedJob.clientWorkOrder },
                    {
                      label: t("Address:"),
                      value: [selectedJob.address, selectedJob.city, selectedJob.state].filter(Boolean).join(", "),
                      wide: true,
                    },
                    { label: t("Start Date:"), value: selectedJob.startDate },
                    { label: t("End Date:"), value: selectedJob.endDate },
                    {
                      label: t("Time Window:"),
                      value:
                        selectedJob.startTimeMode === "custom"
                          ? t("Custom ({time})", { time: selectedJob.startTime })
                          : selectedJob.startTime === "ANY" || !selectedJob.startTime
                          ? t("ANY")
                          : selectedJob.startTime,
                    },
                    { label: t("End Time:"), value: selectedJob.endTime || t("N/A") },
                    {
                      label: t("Recurrence:"),
                      value: selectedJob.recurrence?.type
                        ? t(RECURRENCE_LABELS[selectedJob.recurrence.type] || selectedJob.recurrence.type)
                        : t("One-Time"),
                    },
                    {
                      label: t("Days:"),
                      hidden: selectedJob.recurrence?.type !== "weekly",
                      value: (selectedJob.recurrence?.daysOfWeek || [])
                        .map((d) => t(["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][d]))
                        .join(", "),
                    },
                    {
                      label: t("Custom Dates:"),
                      hidden: selectedJob.recurrence?.type !== "customDates",
                      value: (selectedJob.recurrence?.customDates || []).join(", "),
                      wide: true,
                    },
                    {
                      label: t("Planned:"),
                      hidden: !(selectedJob.jobPlan?.estimatedDays || selectedJob.jobPlan?.estimatedLabor),
                      value: `${
                        selectedJob.jobPlan?.estimatedDays
                          ? t("{count} day(s)", { count: selectedJob.jobPlan.estimatedDays })
                          : "—"
                      } · ${
                        selectedJob.jobPlan?.estimatedLabor
                          ? t("{count} people", { count: selectedJob.jobPlan.estimatedLabor })
                          : "—"
                      }`,
                    },
                    {
                      label: t("Service Type:"),
                      value: selectedJob.serviceType ? (
                        <TranslatedText text={selectedJob.serviceType} showToggle={false} />
                      ) : (
                        ""
                      ),
                    },
                    {
                      label: t("Scope:"),
                      value: selectedJob.scope ? <TranslatedText text={selectedJob.scope} /> : "",
                      wide: true,
                    },
                  ]}
                />

                {getDurationMismatch(selectedJob) && (
  <p style={{ color: "#b45309", fontWeight: 700 }}>
    ⚠️ {t("Scheduled days do not match estimated job duration")}
  </p>
)}

                <div>
  <strong>{t("Job Description:")}</strong>
  <div
    style={{
      marginTop: 8,
      padding: "10px 12px",
      border: "1px solid #d1d5db",
      borderRadius: 8,
      background: "#f9fafb",
      whiteSpace: "normal",
    }}
  >
    <TranslatedText
      text={selectedJob.jobDescription}
      render={(desc) => parseFormattedJobDescription(desc)}
      block
    />
  </div>
</div>
                {/* ===== Materials ===== */}
<h3 style={{ marginTop: 16 }}>{t("Materials")}</h3>

{getMaterialsForJob(selectedJob).length > 0 ? (
  <div style={{ display: "grid", gap: 8 }}>
    {getMaterialsForJob(selectedJob).map((material, index) => {
      const estimatedCost =
        Number(material.qty || 0) * Number(material.costPer || 0);

      return (
        <div
          key={index}
          style={{
            display: "grid",
            gridTemplateColumns: "32px minmax(0, 1fr)",
            gap: 10,
            alignItems: "start",
            padding: 10,
            border: "1px solid #d1d5db",
            borderRadius: 10,
            background: material.purchased ? "#ecfdf5" : "#f8fafc",
          }}
        >
          <button
            type="button"
            title={material.purchased ? t("Mark as not purchased") : t("Mark as purchased")}
            aria-label={material.purchased ? t("Mark as not purchased") : t("Mark as purchased")}
            onClick={() =>
              updateMaterialStatus(selectedJob.id, index, {
                purchased: !material.purchased,
                purchasedAt: !material.purchased
                  ? new Date().toISOString()
                  : "",
                purchasedBy: !material.purchased
                  ? userFullName || "User"
                  : "",
              })
            }
            className={`material-toggle${material.purchased ? " material-toggle--checked" : ""}`}
          >
            <svg
              className="material-toggle__check"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#fff"
              strokeWidth="3.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <polyline points="4 12 9 17 20 6" />
            </svg>
          </button>

          <div>
            <div style={{ fontWeight: 800 }}>
              {material.name || material.product || t("Unnamed material")}
            </div>
            <div style={{ fontSize: 13, color: "#475569", marginTop: 3 }}>
              {t("Qty:")} {material.qty || 0}
              {!isTech && material.costPer !== "" && material.costPer !== undefined
                ? ` • ${t("Cost Per:")} ${Number(material.costPer || 0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}`
                : ""}
              {!isTech && material.costPer !== "" && material.costPer !== undefined
                ? ` • ${t("Estimated:")} ${estimatedCost.toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                  })}`
                : ""}
            </div>
            {material.purchaseLocation && (
              <div style={{ fontSize: 13, marginTop: 3 }}>
                <strong>{t("Purchase Location:")}</strong> {material.purchaseLocation}
              </div>
            )}
            {material.notes && (
              <div style={{ fontSize: 13, marginTop: 3 }}>
                <strong>{t("Material Notes:")}</strong> <TranslatedText text={material.notes} />
              </div>
            )}
            <div
              style={{
                fontSize: 12,
                marginTop: 5,
                fontWeight: 800,
                color: material.purchased ? "#166534" : "#64748b",
              }}
            >
              {material.purchased ? t("Purchased") : t("Not Purchased")}
            </div>

            {Array.isArray(material.files) && material.files.length > 0 && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {material.files.map((file, fileIndex) => (
                  <a
                    key={fileIndex}
                    href={file.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={file.name}
                  >
                    {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                      <img
                        src={file.url}
                        alt={file.name}
                        style={{
                          width: 56,
                          height: 56,
                          objectFit: "cover",
                          borderRadius: 6,
                          border: "1px solid #cbd5e1",
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          width: 56,
                          height: 56,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "1px solid #cbd5e1",
                          borderRadius: 6,
                          background: "#fff",
                          fontSize: 10,
                          textAlign: "center",
                          padding: 4,
                          overflow: "hidden",
                        }}
                      >
                        {file.name}
                      </div>
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>
        </div>
      );
    })}

    {!isTech && (
    <div
      style={{
        textAlign: "right",
        fontWeight: 900,
        paddingTop: 4,
      }}
    >
      {t("Total Estimated Material Cost:")}{" "}
      {getMaterialsForJob(selectedJob)
        .reduce(
          (sum, material) =>
            sum +
            Number(material.qty || 0) * Number(material.costPer || 0),
          0
        )
        .toLocaleString("en-US", {
          style: "currency",
          currency: "USD",
        })}
    </div>
    )}
  </div>
) : (
  <p style={{ fontStyle: "italic" }}>{t("No materials listed.")}</p>
)}

                <p>
  <strong>{t("Client Phone:")}</strong> {selectedJob.clientPhone || "N/A"}
</p>
<p>
  <strong>{t("Client Email:")}</strong> {selectedJob.clientEmail || "N/A"}
</p>
<p>
  <strong>{t("Tech Assign:")}</strong>{" "}
  {Array.isArray(selectedJob.techAssign)
    ? selectedJob.techAssign.join(", ")
    : selectedJob.techAssign || t("Unassigned")}
</p>
{/* Every job in this chain (3101, 3101-2, ...), first to last. Live. */}
<LinkedJobsButton
  job={selectedJob}
  onOpenJob={(j) => {
    setIsEditing(false);
    setSelectedJob(jobs.find((x) => x.id === j.id) || j);
  }}
/>
                <p>
                  <strong>{t("Status:")}</strong> {selectedJob.status ? t(selectedJob.status) : ""}
                </p>

                {selectedJob.isProject && (
  <p
    style={{
      background: "#ecfeff",
      border: "1px solid #06b6d4",
      padding: 8,
      borderRadius: 8,
      fontWeight: 800,
    }}
  >
    🏗️ {t("Project Job — Day {day}", { day: selectedJob.projectDayNumber || 1 })} —{" "}
    {t(selectedJob.projectStatus || "Active")}
  </p>
)}

                <p>
  <strong>{t("Created On:")}</strong>{" "}
  {selectedJob.creationDate
    ? new Date(
        selectedJob.creationDate.seconds
          ? selectedJob.creationDate.seconds * 1000
          : selectedJob.creationDate
      ).toLocaleString(localeFor(lang))
    : t("N/A")}
</p>

<p>
  <strong>{t("Created By:")}</strong>{" "}
  {selectedJob.createdBy
    ? `${selectedJob.createdBy.firstName} ${selectedJob.createdBy.lastName}`
    : t("Unknown")}
</p>

<h3 style={{ marginTop: 16 }}>{t("Receipts")}</h3>

{/* Dollar amounts are office-only; techs still see the receipts themselves. */}
{!isTech && (
<p>
  <strong>{t("Receipt Total:")}</strong> {getReceiptsTotal(selectedJob).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  })}
</p>
)}

{Array.isArray(selectedJob.receipts) && selectedJob.receipts.length > 0 ? (
  <ul>
    {selectedJob.receipts.map((receipt, index) => (
      <li key={index} style={{ marginBottom: 8 }}>
        {!isTech && (
          <strong>
            {Number(receipt.amount || 0).toLocaleString("en-US", {
              style: "currency",
              currency: "USD",
            })}
          </strong>
        )}

        {receipt.note && (
          <>
            {!isTech && " — "}
            <TranslatedText text={receipt.note} hint={receipt.lang} />
          </>
        )}
        {isTech && !receipt.note && <span>{t("Receipt")} {index + 1}</span>}

        {receipt.uploadedBy && (
          <div style={{ fontSize: 12, color: "#475569" }}>
            {t("Uploaded by {name}", { name: receipt.uploadedBy })}
          </div>
        )}

        {receipt.fileURL && (
          <div>
            <a
              href={receipt.fileURL}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontWeight: 700, color: "#1d4ed8" }}
            >
              {t("View Receipt Image")}
            </a>
          </div>
        )}
      </li>
    ))}
  </ul>
) : (
  <p style={{ fontStyle: "italic" }}>{t("No receipts added.")}</p>
)}


                <h3>{t("Notes")}</h3>
                <div className="notes-container">
                {getJobNotesForDisplay(selectedJob).length > 0 ? (
  <ul>
    {getJobNotesForDisplay(selectedJob).map((note, index) => (
      <li key={index}>
        <strong>
          {note.author} ({note.timestamp}):
        </strong>{" "}
        <TranslatedText text={note.text} hint={note.lang} />
        {note.files &&
          note.files.map((file, fileIndex) => (
            <p key={fileIndex}>
              <a
                href={file.fileURL}
                target="_blank"
                rel="noopener noreferrer"
                style={{ fontWeight: 700, color: "#1d4ed8" }}
              >
                {file.fileName || t("View Attachment {n}", { n: fileIndex + 1 })}
              </a>
            </p>
          ))}
      </li>
    ))}
  </ul>
) : (
  <p>{t("No notes yet for this occurrence.")}</p>
)}
                </div>

                {canSeeSupervisorNotes && (
  <div
    style={{
      marginTop: 16,
      padding: 12,
      border: "2px solid #7c3aed",
      borderRadius: 10,
      background: "#f5f3ff",
    }}
  >
    <h3 style={{ marginTop: 0 }}>Supervisor Notes</h3>

    {getSupervisorNotesForDisplay(selectedJob).length > 0 ? (
      <ul style={{ paddingLeft: 18 }}>
        {getSupervisorNotesForDisplay(selectedJob).map((note) => (
          <li
            key={note.id}
            style={{
              marginBottom: 10,
              padding: 8,
              background: "white",
              border: "1px solid #c4b5fd",
              borderRadius: 8,
            }}
          >
           <div>
  <div style={{ whiteSpace: "pre-wrap" }}>{note.text}</div>

  <div style={{ fontSize: 12, color: "#4b5563", marginTop: 4 }}>
    Added by {note.author || "User"}
    {note.authorRole ? ` (${note.authorRole})` : ""}{" "}
    {note.createdAt
      ? `on ${new Date(note.createdAt).toLocaleString()}`
      : ""}
  </div>
</div>
          </li>
        ))}
      </ul>
    ) : (
      <p>No supervisor notes yet.</p>
    )}
  </div>
)}

                {selectedJob.fileURLs && selectedJob.fileURLs.length > 0 && (
  <div>
    <h3>{t("Uploaded Files")}</h3>
    <div className="uploaded-files">
      {selectedJob.fileURLs.map((fileURL, index) => (
        <div key={index}>
          <a
            href={fileURL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontWeight: 700, color: "#1d4ed8" }}
          >
            {selectedJob.fileNames?.[index] || t("View File {n}", { n: index + 1 })}
          </a>
          <p>
            {selectedJob.fileNames && selectedJob.fileNames[index]}
          </p>
        </div>
      ))}
    </div>
  </div>
)}

{selectedJob.newFiles && selectedJob.newFiles.length > 0 && (
  <div>
    <h3>{t("New Attachments")}</h3>
    <div className="new-attachments">
      {selectedFiles.map((file, index) => (
        <div key={index} style={{ marginBottom: 6 }}>
          <span style={{ fontWeight: 700, color: "#1d4ed8" }}>
            {file.name}
          </span>
        </div>
      ))}
    </div>
  </div>
)}

{/* Job Money: contract total, change orders, billed / paid, linked WOs (Admin + Manager) */}
{canAccessAdminPortal && !isEditing && (
  <JobMoneyPanel key={selectedJob.id} job={selectedJob} canEdit />
)}

{/* Sub Pay: Admin sees every sub on the job; a sub sees only their own pay */}
{!isEditing &&
  (canSeeSubPay(userRole) ||
    (currentUserData && isSubcontractor(currentUserData) && isAssignedToMe(selectedJob))) && (
    <SubPayPanel
      key={`subpay-${selectedJob.id}`}
      job={selectedJob}
      userRole={userRole}
      currentUser={currentUserData}
      onJobUpdated={(patch) => {
        const id = selectedJob.id;
        setSelectedJob((prev) => (prev && prev.id === id ? { ...prev, ...patch } : prev));
        setJobs((prev) => prev.map((j) => (j.id === id ? { ...j, ...patch } : j)));
      }}
    />
  )}

{/* Techs (and subs) never see the Supervisor actions: no History, no totals. */}
{!isTech && (
<CollapsibleActionSection title={t("Supervisor")} tone="supervisor" defaultOpen={false}>
  {["Admin", "Manager", "Assistant"].includes(userRole) && (
    <button
      className="grey-button"
      onClick={() => {
        setEditedJob({
          ...selectedJob,
        
          // ✅ ENSURE jobPlan ALWAYS EXISTS
          jobPlan: {
            estimatedDays: selectedJob.jobPlan?.estimatedDays || "",
            estimatedLabor: selectedJob.jobPlan?.estimatedLabor || "",
          },
        
          techAssign: Array.isArray(selectedJob.techAssign)
            ? selectedJob.techAssign
            : [selectedJob.techAssign || ""],
        
          startTimeMode: selectedJob.startTime?.includes(":")
            ? "custom"
            : "preset",
        
          companyUseMode:
            ["JMC", "DCS", "OGR", "MHI"].includes(selectedJob.companyUse)
              ? selectedJob.companyUse
              : (selectedJob.companyUse ? "Other" : ""),
        });
        

        setIsEditing(true);
      }}
    >
      Edit
    </button>
  )}

  {canSeeSupervisorNotes && (
  <button className="grey-button" onClick={addSupervisorNote}>
    Add Supervisor Note
  </button>
)}

  <button className="grey-button" onClick={() => setShowHistoryPopup(true)}>
    {t("History")}
  </button>

  {!isTech && !isRecurringJob(selectedJob) && selectedJob.status !== "Completed" && (
  <button
    className="yellow-button"
    onClick={() => {
      setContinueDate("");
      setShowContinuePopup(true);
    }}
  >
    Continue
  </button>
)}

  {/* Duplicate: next WO like Continue, but the original stays Active */}
  {!isTech && !isRecurringJob(selectedJob) && ["Admin", "Manager", "Assistant"].includes(userRole) && (
  <button
    className="purple-button"
    style={{ background: "#7c3aed", backgroundImage: "none", borderColor: "#7c3aed", color: "#fff" }}
    onClick={() => setDuplicateJob(selectedJob)}
  >
    Duplicate
  </button>
)}

  <button
    className={
      jobTotals[String(selectedJob.wo ?? selectedJob.id)]
        ? "yellow-button"
        : "green-button"
    }
    onClick={() => openTotalPopup(selectedJob)}
  >
    {jobTotals[String(selectedJob.wo ?? selectedJob.id)]
      ? t("Change Total")
      : t("Add Total")}
  </button>

  {/* 🔒 CLOSE JOB — Admin + Manager ONLY */}
  {canDelete && (
    <button
      className="black-button"
      onClick={handleDeleteJob}
    >
      Close Job
    </button>
  )}
</CollapsibleActionSection>
)}

<div className="modal-buttons">
  <button
    className="blue-button"
    onClick={() => setShowNotePopup(true)}
  >
    {t("Add Note")}
  </button>

  <button
    className="green-button"
    disabled={isTech && techCompletionRequest?.status === "pending"}
    onClick={() => {
      // Techs must check in at a job before they can complete it.
      if (isTech && !jobVisits.hasVisitFor(selectedJob)) {
        alert(
          t("Check in to this job first.\n\nTap Check In when you arrive so your time and location are recorded, then mark it completed.")
        );
        return;
      }
      setShowVerificationPopup(true);
    }}
  >
    {isTech && techCompletionRequest?.status === "pending"
      ? t("Sent to Office ✓")
      : t("Mark Job as Completed")}
  </button>

  {selectedJob.isProject && ["Admin", "Manager", "Assistant"].includes(userRole) && (
  <button
    className="red-button"
    onClick={markProjectFullyCompleted}
  >
    Project Completed
  </button>
)}
</div>

<div className="modal-buttons">

<button
  className="blue-button"
  onClick={() => setShowReceiptPopup(true)}
>
  {t("Add Receipt")}
</button>

{/* Materials are office-only. The Checklist shows for techs the Admin
    picked in Staff Settings (or every tech). */}
{!isTech && (
<button className="blue-button" onClick={() => setShowMaterialsPopup(true)}>
  {t("Add / Edit Materials")}
</button>
)}

{checklistAccess.canSee && (
<button
              className="yellow-button"
              onClick={() =>
                navigate(`/JobChecklist/${selectedJob.id}`, {
                  state: {
                    returnTo: `${location.pathname}${location.search || ""}`,
                    reopenJobId: selectedJob.id,
                    reopenOccurrenceDate: selectedJob.occurrenceDate || selectedJob.startDate || "",
                  },
                })
              }
            >
              📝 {t("Checklist")}
            </button>
)}

  <button className="red-button" onClick={closeModal}>
    {t("Close")}
  </button>
</div>

                 
              </>
            )}
            </div>
          </div>
        </>
      )}

{showHistoryPopup && selectedJob && !isTech && (
  <JobHistoryPopup
    job={selectedJob}
    jobs={jobs}
    onClose={() => setShowHistoryPopup(false)}
  />
)}

{showMaterialsPopup && !isTech && (
  <>
    <div
      className="modal-overlay"
      onClick={() => setShowMaterialsPopup(false)}
    />
    <div
      className="modal"
      style={{
        width: "min(1050px, 96vw)",
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
      }}
    >
      <h3 style={{ marginBottom: 4 }}>{t("Add / Edit Materials")}</h3>
      <p style={{ marginTop: 0, color: "#475569", fontSize: 13 }}>
        {isTech
          ? t("Check Purchased when the material has been bought.")
          : t("Check Purchased when the material has been bought. Estimated Material Cost is calculated automatically from Qty × Cost Per.")}
      </p>

      <div style={{ display: "grid", gap: 12 }}>
        {materialsDraft.map((material, index) => {
          const estimatedCost = getEstimatedMaterialCost(material);

          return (
            <div
              key={index}
              style={{
                border: material.purchased
                  ? "2px solid #22c55e"
                  : "1px solid #cbd5e1",
                borderRadius: 12,
                padding: 12,
                background: material.purchased ? "#ecfdf5" : "#f8fafc",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 8,
                    fontWeight: 900,
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(material.purchased)}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        purchased: event.target.checked,
                        purchasedAt: event.target.checked
                          ? new Date().toISOString()
                          : "",
                        purchasedBy: event.target.checked
                          ? userFullName || "User"
                          : "",
                      })
                    }
                    style={{ width: 20, height: 20 }}
                  />
                  {t("Purchased")}
                </label>

                <button
                  type="button"
                  className="red-button"
                  onClick={() => removeMaterialDraft(index)}
                  style={{ width: "auto" }}
                >
                  {t("Remove")}
                </button>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                  gap: 10,
                }}
              >
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  {t("Product")}
                  <input
                    type="text"
                    placeholder={t("Example: 2x4 pressure-treated lumber")}
                    value={material.name || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, { name: event.target.value })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  {t("Qty")}
                  <input
                    type="number"
                    min="0"
                    step="any"
                    value={material.qty ?? ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        qty:
                          event.target.value === ""
                            ? ""
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>

                {!isTech && (<>
                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  {t("Cost Per")}
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={material.costPer ?? ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        costPer:
                          event.target.value === ""
                            ? ""
                            : Number(event.target.value),
                      })
                    }
                  />
                </label>

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  {t("Estimated Material Cost")}
                  <input
                    type="text"
                    readOnly
                    value={estimatedCost.toLocaleString("en-US", {
                      style: "currency",
                      currency: "USD",
                    })}
                    style={{
                      background: "#e2e8f0",
                      fontWeight: 900,
                      cursor: "not-allowed",
                    }}
                  />
                </label>
                </>)}

                <label style={{ display: "grid", gap: 5, fontWeight: 800 }}>
                  {t("Purchase Location")}
                  <input
                    type="text"
                    placeholder={t("Example: Home Depot - Leesburg")}
                    value={material.purchaseLocation || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, {
                        purchaseLocation: event.target.value,
                      })
                    }
                  />
                </label>

                <label
                  style={{
                    display: "grid",
                    gap: 5,
                    fontWeight: 800,
                    gridColumn: "1 / -1",
                  }}
                >
                  {t("Material Notes")}
                  <textarea
                    rows="3"
                    placeholder={t("Color, size, model number, pickup instructions, or other notes")}
                    value={material.notes || ""}
                    onChange={(event) =>
                      updateMaterialDraft(index, { notes: event.target.value })
                    }
                  />
                </label>
              </div>

              <div style={{ marginTop: 12 }}>
                <div style={{ fontWeight: 800, marginBottom: 6 }}>
                  {t("Photos / Files")}
                </div>

                {Array.isArray(material.files) && material.files.length > 0 && (
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
                    {material.files.map((file, fileIndex) => (
                      <div key={fileIndex} style={{ position: "relative" }}>
                        <a href={file.url} target="_blank" rel="noopener noreferrer" title={file.name}>
                          {/\.(jpe?g|png|gif|webp)$/i.test(file.name || "") ? (
                            <img
                              src={file.url}
                              alt={file.name}
                              style={{ width: 64, height: 64, objectFit: "cover", borderRadius: 6, border: "1px solid #cbd5e1" }}
                            />
                          ) : (
                            <div
                              style={{
                                width: 64,
                                height: 64,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                border: "1px solid #cbd5e1",
                                borderRadius: 6,
                                background: "#fff",
                                fontSize: 10,
                                textAlign: "center",
                                padding: 4,
                                overflow: "hidden",
                              }}
                            >
                              {file.name}
                            </div>
                          )}
                        </a>
                        <button
                          type="button"
                          onClick={() => removeMaterialFile(index, fileIndex)}
                          title={t("Remove file")}
                          style={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            width: 20,
                            height: 20,
                            borderRadius: "50%",
                            border: "1px solid #b91c1c",
                            background: "#fee2e2",
                            color: "#b91c1c",
                            fontSize: 13,
                            lineHeight: 1,
                            padding: 0,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <input
                  type="file"
                  multiple
                  accept="image/*,.pdf"
                  onChange={(event) => uploadMaterialFiles(index, event.target.files)}
                />
              </div>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        className="blue-button"
        onClick={addMaterial}
        style={{ marginTop: 12 }}
      >
        + {t("Add Material")}
      </button>

      {!isTech && (
      <div
        style={{
          marginTop: 14,
          padding: 12,
          borderRadius: 10,
          background: "#eff6ff",
          border: "1px solid #93c5fd",
          textAlign: "right",
          fontWeight: 900,
          fontSize: 16,
        }}
      >
        {t("Total Estimated Material Cost:")}{" "}
        {materialsDraft
          .reduce(
            (sum, material) => sum + getEstimatedMaterialCost(material),
            0
          )
          .toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
          })}
      </div>
      )}

      <div className="modal-buttons">
        <button className="green-button" onClick={saveMaterials}>
          {t("Save Materials")}
        </button>
        <button
          className="red-button"
          onClick={() => setShowMaterialsPopup(false)}
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  </>
)}

{showReceiptPopup && (
  <>
    <div
      className="modal-overlay"
      onClick={() => setShowReceiptPopup(false)}
    ></div>

    <div className="modal">
      <h2>{t("Add Receipt")}</h2>

      <label>{t("Receipt Amount")}</label>
      <input
        type="number"
        value={receiptAmount}
        onChange={(e) => setReceiptAmount(e.target.value)}
        placeholder={t("Enter receipt amount")}
      />

      <label>{t("Receipt Note")}</label>
      <input
        type="text"
        value={receiptNote}
        onChange={(e) => setReceiptNote(e.target.value)}
        placeholder={t("Example: Home Depot materials")}
      />

      <label>{t("Upload Receipt Image")}</label>
      <input
        type="file"
        accept="image/*,.pdf"
        onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
      />

      {receiptFile && (
        <p style={{ fontSize: 12, color: "#475569" }}>
          {t("Selected: {name}", { name: receiptFile.name })}
        </p>
      )}

      <div className="modal-buttons">
        <button
          className="green-button"
          onClick={saveReceipt}
          disabled={savingReceipt}
        >
          {savingReceipt ? t("Saving...") : t("Save Receipt")}
        </button>

        <button
          className="red-button"
          onClick={() => setShowReceiptPopup(false)}
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  </>
)}

      {showNotePopup && (
        <>
          <div className="modal-overlay" onClick={closeNotePopup}></div>
          <div className="modal">
            <h2>{t("Add Note")}</h2>
            <textarea
              className="form-container"
              rows="4"
              placeholder={t("Enter your note here...")}
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
            ></textarea>
            <input
              type="file"
              accept="image/*, .pdf"
              onChange={handleFileChange}
              multiple
            />
            <div className="modal-buttons">
              <button
                className="green-button"
                onClick={saveNote}
                disabled={uploading}
              >
                {uploading ? <LoadingSpinner label={t("Saving note...")} /> : t("Save")}
              </button>
              <button className="red-button" onClick={closeNotePopup}>
                {t("Cancel")}
              </button>
            </div>
          </div>
        </>
      )}

{duplicateJob && (
  <DuplicateJobPopup
    job={duplicateJob}
    createdBy={{
      uid: userId || "",
      firstName: userFullName?.split(" ")?.[0] || "",
      lastName: userFullName?.split(" ")?.slice(1).join(" ") || "",
      fullName: userFullName || "User",
    }}
    onClose={() => setDuplicateJob(null)}
    onDuplicated={(created) => {
      setDuplicateJob(null);
      setJobs((prev) => [...prev, created.job]);
      alert(`Duplicate created: WO ${created.wo} on ${created.job.startDate}.\nThe original job is still Active.`);
    }}
  />
)}

{showContinuePopup && selectedJob && (
  <>
    <div
      className="modal-overlay"
      onClick={() => setShowContinuePopup(false)}
    ></div>

    <div className="modal">
      <h3>Continue Job</h3>

      <p>
        This will complete the current job for{" "}
        <strong>{toDateKey(selectedJob.startDate) || "the current date"}</strong>{" "}
        and create a new active job copy.
      </p>

      <label>New Continue Date:</label>
      <input
        type="date"
        value={continueDate}
        onChange={(e) => setContinueDate(e.target.value)}
      />

      {continueDate && (
        <p style={{ color: "#b45309", fontWeight: 700 }}>
          Warning: You are about to complete this job for today and schedule a
          new job for {continueDate}. Do you want to continue?
        </p>
      )}

      <div className="button-group">
        <button
          className="shortgreen-button"
          onClick={continueJobToNewDate}
          disabled={continuingJob || !continueDate}
        >
          {continuingJob ? "Saving..." : "Yes"}
        </button>

        <button
          className="shortred-button"
          onClick={() => {
            setShowContinuePopup(false);
            setContinueDate("");
          }}
          disabled={continuingJob}
        >
          No
        </button>
      </div>
    </div>
  </>
)}

      {showVerificationPopup && isTech && selectedJob && (
        <TechCompleteDialog
          job={selectedJob}
          techName={userFullName}
          onClose={closeVerificationPopup}
          onSubmitted={async (saved) => {
            setTechCompletionRequest(saved);
            closeVerificationPopup();
            // Same auto check-out a completed job used to do for techs.
            try {
              await jobVisits.checkOutIfAt(selectedJob, "job_completed");
            } catch (err) {
              console.warn("Check-out after completion failed:", err);
            }
          }}
        />
      )}
      {showVerificationPopup && !isTech && (
        <>
          <div className="modal-overlay" onClick={closeVerificationPopup}></div>
          <div className="modal">
            <h3>Are you sure you want to complete this job?</h3>
            <div className="button-group">
              <button className="shortgreen-button" onClick={markJobCompleted}>
                Yes
              </button>
              <button
                className="shortred-button"
                onClick={closeVerificationPopup}
              >
                No
              </button>
            </div>
          </div>
        </>
      )}

<HowToGuide open={showGuide} onClose={() => setShowGuide(false)} />

{showMoreMenu && (
  <>
    {/* backdrop */}
<div
  className="modal-overlay"
  onClick={() => setShowMoreMenu(false)}
  style={{
    position: "fixed",
    inset: 0,
    zIndex: 999,
  }}
></div>


    {/* menu */}
    <div
      style={{
        position: "fixed",
        top: "10%",
        left: "50%",
        transform: "translateX(-50%)",
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 10px 25px rgba(0,0,0,0.2)",
        zIndex: 1001,
        minWidth: 240,
        maxWidth: "calc(100vw - 24px)",
        maxHeight: "80vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* scrollable body */}
      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowY: "auto",
          overscrollBehavior: "contain",
          WebkitOverflowScrolling: "touch",
          padding: 12,
        }}
      >
      {!isTech && (
        <button
          onClick={() => {
            setShowMoreMenu(false);
            setShowGuide(true);
          }}
          style={{
            width: "100%",
            padding: "12px 14px",
            marginBottom: 8,
            borderRadius: 10,
            border: "1px solid #6d28d9",
            background: "#6d28d9",
            color: "#fff",
            fontWeight: 700,
            cursor: "pointer",
            textAlign: "left",
          }}
        >
          ❓ {t("How do I…?")}
        </button>
      )}
      {rightNavTabs
  .filter((x) => !(isTech && x.tab === "clock-time"))
  .map((x) => (

  <button
    key={x.path}
    onClick={() => {
      setShowMoreMenu(false);
      setActiveTab(x.tab);
    }}
    
    style={{
      width: "100%",
      padding: "12px 14px",
      marginBottom: 8,

      borderRadius: 10,
      border: "1px solid #1d4ed8",
      background: "#e0e7ff",
      color: "#0f172a",

      fontWeight: 700,
      cursor: "pointer",
      textAlign: "left",
    }}
    onMouseEnter={(e) =>
      (e.currentTarget.style.background = "#c7d2fe")
    }
    onMouseLeave={(e) =>
      (e.currentTarget.style.background = "#e0e7ff")
    }
  >
    {t(x.label)}
  </button>
))}
      </div>
      {/* end scrollable body */}

      {/* pinned footer */}
      <div
        style={{
          flex: "0 0 auto",
          textAlign: "right",
          padding: 12,
          borderTop: "1px solid #e5e7eb",
          background: "#fff",
        }}
      >
        <button
          onClick={() => setShowMoreMenu(false)}
          className="red-button"
        >
          {t("Close")}
        </button>
      </div>
    </div>
  </>
)}

{/* After Continue: confirm the new job's location (waits for the billing popup) */}
{fencePromptJob && !billingPromptJob && (
  <JobLocationConfirm
    job={fencePromptJob}
    heading="Confirm location for the continued job"
    onClose={() => setFencePromptJob(null)}
    onSaved={(jobFence) => applyJobFence(fencePromptJob.id, jobFence)}
  />
)}

{billingPromptJob && (
  <BillingTypePopup
    job={billingPromptJob}
    title="Job completed. How should it be invoiced?"
    onClose={(result) => {
      const done = billingPromptJob;
      setBillingPromptJob(null);
      if (result?.created || result?.existed) {
        setJobs((prev) =>
          prev.map((j) =>
            j.id === done.id ? { ...j, approvedInvoice: true, approvedInvoiceId: result.invoiceId } : j
          )
        );
      }
      if (result?.billLater) {
        // Billed on the continuation WO - no invoice on this one.
        setJobs((prev) =>
          prev.map((j) =>
            j.id === done.id
              ? { ...j, billLater: true, billLaterToWO: result.toWO, billLaterToJobId: result.toJobId }
              : j
          )
        );
      }
      if (result?.created) alert("Invoice created in Approved Invoices.");
    }}
  />
)}

{showViewAsPicker && (
  <ViewAsPicker
    users={usersList}
    onClose={() => setShowViewAsPicker(false)}
    onPick={(person) => {
      setShowViewAsPicker(false);
      startViewAs(person);
    }}
  />
)}

{showTotalPopup && !isTech && (
  <>
    <div className="modal-overlay" onClick={() => setShowTotalPopup(false)} />
    <div className="modal">
      <h3>{jobTotals[String(selectedJob.wo ?? selectedJob.id)]
 ? t("Change Total") : t("Add Total")}</h3>

      {totalLedger ? (
        <p style={{ fontSize: 13, color: "#475569", textAlign: "left", margin: "4px 0 10px" }}>
          This job's total is kept in <b>Job Money</b>. Saving here corrects the{" "}
          <b>original</b> contract total (now ${computeContractTotals(totalLedger).original.toLocaleString()}).
          {computeContractTotals(totalLedger).changeOrders || computeContractTotals(totalLedger).adjustments
            ? ` Change orders and adjustments stay on top of it (current total $${computeContractTotals(totalLedger).current.toLocaleString()}).`
            : ""}{" "}
          The approved proposal and any sent or paid invoices are not changed.
        </p>
      ) : null}

      <label>{totalLedger ? t("Original contract total ($)") : t("Total Amount ($)")}</label>
      <input
        type="number"
        value={totalInput}
        onChange={(e) => setTotalInput(e.target.value)}
        placeholder={t("Enter amount")}
      />

      {totalLedger && (
        <>
          <label>{t("Reason (required)")}</label>
          <input
            type="text"
            value={totalReason}
            onChange={(e) => setTotalReason(e.target.value)}
            placeholder="e.g. typo on proposal, archive clean-up"
          />
        </>
      )}

      <div className="modal-buttons">
        <button className="green-button" onClick={saveTotal}>
          {t("Save")}
        </button>
        <button className="red-button" onClick={() => setShowTotalPopup(false)}>
          {t("Cancel")}
        </button>
      </div>
    </div>
  </>
)}

{isStaff && (
  <MissedJobsReview
    mode="modal"
    open={missedOpen}
    rows={missedRows}
    snoozedKeys={missedSnooze.keys}
    onRemindLater={remindMissedLater}
    onClose={() => setMissedOpen(false)}
    onSnoozeKeys={snoozeMissedKeys}
    onResults={applyMissedResults}
    onOpenJob={openMissedJob}
    staffName={userFullName}
    pendingDoneCount={missedRows.pendingDoneCount || 0}
  />
)}

{isStaff && reminderOpen && (
  <JobRemindersReview
    key={reminderOpen}
    kind={reminderOpen}
    mode="modal"
    open
    rows={homeReminders.rows[reminderOpen] || []}
    snoozedKeys={reminderSnooze[reminderOpen]?.keys || []}
    onRemindLater={() => remindLater(reminderOpen)}
    onClose={() => setReminderOpen("")}
    onSnoozeKeys={(keys) => snoozeReminderRowKeys(reminderOpen, keys)}
    staffNames={reminderStaffNames}
    staffName={userFullName}
    userRole={userRole}
    onJobPatch={patchReminderJob}
    onDismissed={homeReminders.addDismissal}
    onOpenJob={openReminderJob}
    onRenumbered={() => {
      homeReminders.reload();
      setHomeReloadKey((k) => k + 1);
    }}
    scopeNote={
      reminderOpen === "dupWO"
        ? "Every WO number used at more than one property. Undo any change in Settings → WO Totals Repair."
        : reminderOpen === "proposalJob"
        ? "Jobs created from approved proposals in the last 60 days. Job History → Reminders has every one."
        : reminderOpen === "noTech"
        ? "Jobs from the last 14 days through next week. Job History → Reminders has every job."
        : reminderOpen === "clockedIn"
        ? "The last 7 days. Job History → Reminders goes further back."
        : "The last 60 days. Job History → Reminders has every job."
    }
  />
)}

    </div>
  );
};

export default Home;